import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Truck, MapPin, CheckCircle, AlertTriangle, ArrowLeft, Play, Flag, Check,
  Clock, Globe, ShieldCheck, ChevronRight, ChevronLeft, Calendar, User, Phone, Mail, Award,
  Navigation, DollarSign, Building, X, Camera, Plus, FileText, File, Info, Image as ImageIcon,
  PenTool, Package, CreditCard, Lock, CheckSquare, Trash2, ChevronDown, RefreshCw,
  Pause, PlayCircle, Map, List, Copy, Layers, Settings, Timer, LogOut, Edit3
} from 'lucide-react';
import MapView from '../components/MapView';
import { formatLocalTimeHHMM, parseToDate, calcDepartureTimeStr } from '../utils/dateUtils';
import { startBackgroundTracking, stopBackgroundTracking } from '../services/trackerService';
import { Capacitor } from '@capacitor/core';
import { Camera as CapCamera, CameraResultType, CameraSource } from '@capacitor/camera';

function formatDateFormatted(date) {
  const frenchDays = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
  const frenchMonths = ['Janv', 'Févr', 'Mars', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sept', 'Oct', 'Nov', 'Déc'];
  return `${frenchDays[date.getDay()]} ${date.getDate()} ${frenchMonths[date.getMonth()]} ${date.getFullYear()}`;
}

function formatDateISO(date) {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function formatDurationSec(totalSec = 0) {
  const h = Math.floor((totalSec || 0) / 3600);
  const m = Math.floor(((totalSec || 0) % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatTimerHHMMSS(totalSec = 0) {
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) {
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function calcHaversineDistanceKm(lat1, lon1, lat2, lon2) {
  if (!lat1 || !lon1 || !lat2 || !lon2) return 0;
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return parseFloat((R * c).toFixed(1));
}

function formatSignerName(lastName = '', firstName = '') {
  const cleanLast = (lastName || '').trim().toUpperCase();
  const cleanFirst = (firstName || '')
    .trim()
    .replace(/(?:^|[\s-])\p{L}/gu, (char) => char.toUpperCase());
  if (cleanLast && cleanFirst) return `${cleanLast} ${cleanFirst}`;
  return cleanLast || cleanFirst || '';
}

function calculateTruckPeakPayload(truck, jobStopsList = []) {
  let deliveriesWeight = 0;
  let deliveriesVolume = 0;

  jobStopsList.forEach(s => {
    const w = parseFloat(s.weight_kg || 0);
    const v = parseFloat(s.volume_m3 || 0);
    if (s.type !== 'pickup') {
      deliveriesWeight += w;
      deliveriesVolume += v;
    }
  });

  let currentWeight = deliveriesWeight;
  let currentVolume = deliveriesVolume;

  let peakWeight = currentWeight;
  let peakVolume = currentVolume;

  jobStopsList.forEach(s => {
    const w = parseFloat(s.weight_kg || 0);
    const v = parseFloat(s.volume_m3 || 0);

    if (s.type === 'pickup') {
      currentWeight += w;
      currentVolume += v;
    } else {
      currentWeight -= w;
      currentVolume -= v;
    }

    if (currentWeight > peakWeight) peakWeight = currentWeight;
    if (currentVolume > peakVolume) peakVolume = currentVolume;
  });

  if (!truck) {
    return {
      peakWeight: Math.round(peakWeight),
      peakVolume: Math.round(peakVolume * 10) / 10,
      maxWeight: '--',
      maxVolume: '--',
      isOverweight: false,
      isOvervolume: false
    };
  }

  const maxWeight = parseFloat(truck.max_payload_weight_kg || 1200);
  const maxVolume = parseFloat(truck.max_payload_volume_m3 || 14.0);

  const isOverweight = peakWeight > maxWeight;
  const isOvervolume = peakVolume > maxVolume;

  return {
    peakWeight: Math.round(peakWeight),
    peakVolume: Math.round(peakVolume * 10) / 10,
    maxWeight: Math.round(maxWeight),
    maxVolume: Math.round(maxVolume * 10) / 10,
    isOverweight,
    isOvervolume
  };
}

function formatWeightPair(peakWeightKg, maxWeightKg) {
  const peak = parseFloat(peakWeightKg || 0);
  const isMaxUnassigned = maxWeightKg === '--';
  const max = isMaxUnassigned ? null : parseFloat(maxWeightKg);

  const useTons = peak >= 1000 || (max !== null && max >= 1000);

  if (useTons) {
    const peakTons = (peak / 1000).toFixed(1);
    if (isMaxUnassigned) {
      return `${peakTons}/--t`;
    }
    const maxTons = (max / 1000).toFixed(1);
    return `${peakTons}/${maxTons}t`;
  }

  const peakKg = Math.round(peak);
  if (isMaxUnassigned) {
    return `${peakKg}/--kg`;
  }
  return `${peakKg}/${Math.round(max)}kg`;
}

function calcHubToStopDriveMins(hub, stop, departureTimeStr) {
  if (!hub || !stop) return 15;
  const km = calcHaversineDistanceKm(hub.latitude, hub.longitude, stop.latitude, stop.longitude);
  return Math.max(3, Math.round(km * 2));
}

function calcLegDriveTimeMins(stopA, stopB) {
  if (!stopA || !stopB) return 10;
  const km = calcHaversineDistanceKm(stopA.latitude, stopA.longitude, stopB.latitude, stopB.longitude);
  return Math.max(2, Math.round(km * 2));
}

function calcStopToHubDriveMins(stop, hub, returnTimeStr) {
  if (!stop || !hub) return 15;
  const km = calcHaversineDistanceKm(stop.latitude, stop.longitude, hub.latitude, hub.longitude);
  return Math.max(3, Math.round(km * 2));
}

function calculateReturnTime(departureTimeStr, displaySec) {
  const [hStr, mStr] = (departureTimeStr || '07:30').split(':');
  const depSec = (parseInt(hStr || 7) * 3600) + (parseInt(mStr || 30) * 60);
  const totalSec = (depSec + (displaySec || 3600)) % (24 * 3600);
  const retH = Math.floor(totalSec / 3600);
  const retM = Math.floor((totalSec % 3600) / 60);
  return `${String(retH).padStart(2, '0')}:${String(retM).padStart(2, '0')}`;
}

function renderStopRealTimeWindows(stop, client, arrivalTimeFormatted) {
  let windows = [];
  if (stop.time_windows && Array.isArray(stop.time_windows) && stop.time_windows.length > 0) {
    windows = stop.time_windows;
  } else if (client?.time_window_start && client?.time_window_end) {
    windows = [{ start: client.time_window_start.slice(0,5), end: client.time_window_end.slice(0,5) }];
  } else {
    windows = [{ start: '08:00', end: '18:00' }];
  }

  const arrTime = stop.actual_arrival ? formatLocalTimeHHMM(stop.actual_arrival) : arrivalTimeFormatted;
  const arrMins = (parseInt(arrTime.split(':')[0] || 8) * 60) + parseInt(arrTime.split(':')[1] || 0);

  let matchedIndex = -1;
  windows.forEach((tw, idx) => {
    const sMins = (parseInt((tw.start || '08:00').split(':')[0] || 8) * 60) + parseInt((tw.start || '08:00').split(':')[1] || 0);
    const eMins = (parseInt((tw.end || '18:00').split(':')[0] || 18) * 60) + parseInt((tw.end || '18:00').split(':')[1] || 0);
    if (arrMins >= sMins && arrMins <= eMins) {
      matchedIndex = idx;
    }
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'center' }}>
      {windows.map((tw, idx) => {
        let pillBg = 'rgba(239, 68, 68, 0.15)';
        let pillColor = '#ef4444';
        let pillBorder = '1px solid #ef4444';

        if (matchedIndex !== -1) {
          if (idx === matchedIndex) {
            pillBg = 'rgba(34, 197, 94, 0.2)';
            pillColor = '#22c55e';
            pillBorder = '1px solid #22c55e';
          } else {
            pillBg = '#1e293b';
            pillColor = '#64748b';
            pillBorder = '1px solid #334155';
          }
        }

        return (
          <span
            key={idx}
            style={{
              background: pillBg,
              color: pillColor,
              border: pillBorder,
              fontSize: '0.68rem',
              fontWeight: '700',
              padding: '1px 5px',
              borderRadius: '4px',
              whiteSpace: 'nowrap'
            }}
          >
            {(tw.start || '08:00').slice(0, 5)} - {(tw.end || '18:00').slice(0, 5)}
          </span>
        );
      })}
    </div>
  );
}

// ----------------------------------------------------
// SIGNATURE CANVAS COMPONENT
// ----------------------------------------------------
function SignaturePadCanvas({ onSave, onCancel, title = "Signature", subtitle = "", onCopyFirstStopSig = null, onCopySig = null, copySigLabel = "Copier la signature" }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0284c7';
  }, []);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height)
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
    setHasDrawn(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    if (!canvas || !hasDrawn) return;
    const dataUrl = canvas.toDataURL('image/png');
    onSave(dataUrl);
  };

  const activeCopyHandler = onCopySig || onCopyFirstStopSig;

  return (
    <div style={{ background: '#0f172a', padding: '16px', borderRadius: '12px', border: '1px solid #334155', display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <span style={{ fontSize: '0.88rem', fontWeight: '800', color: '#38bdf8' }}>{title}</span>
          {subtitle && <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '1px' }}>{subtitle}</div>}
        </div>

        {activeCopyHandler && (
          <button
            type="button"
            onClick={activeCopyHandler}
            style={{
              background: 'rgba(56, 189, 248, 0.15)',
              border: '1px solid #38bdf8',
              color: '#38bdf8',
              padding: '4px 8px',
              borderRadius: '6px',
              fontSize: '0.72rem',
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              cursor: 'pointer'
            }}
            title={copySigLabel}
          >
            <Copy size={12} />
            <span>{copySigLabel}</span>
          </button>
        )}
      </div>

      <div style={{ background: '#ffffff', borderRadius: '8px', overflow: 'hidden', border: '2px dashed #94a3b8', position: 'relative' }}>
        <canvas
          ref={canvasRef}
          width={380}
          height={170}
          style={{ width: '100%', height: '170px', display: 'block', cursor: 'crosshair', touchAction: 'none' }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        <span style={{ position: 'absolute', bottom: '8px', right: '10px', fontSize: '0.68rem', color: '#94a3b8', pointerEvents: 'none' }}>
          Signer avec le doigt ou la souris
        </span>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button
          type="button"
          onClick={clearCanvas}
          style={{ background: '#334155', color: '#cbd5e1', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '0.75rem', cursor: 'pointer' }}
        >
          Effacer
        </button>

        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            onClick={onCancel}
            style={{ background: 'none', border: '1px solid #475569', color: '#94a3b8', padding: '6px 12px', borderRadius: '6px', fontSize: '0.75rem', cursor: 'pointer' }}
          >
            Annuler
          </button>
          <button
            type="button"
            disabled={!hasDrawn}
            onClick={handleSave}
            style={{
              background: hasDrawn ? '#22c55e' : '#334155',
              color: '#ffffff',
              border: 'none',
              padding: '6px 16px',
              borderRadius: '6px',
              fontWeight: '800',
              fontSize: '0.75rem',
              cursor: hasDrawn ? 'pointer' : 'not-allowed'
            }}
          >
            Enregistrer Signature
          </button>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------
// MAIN DRIVER APP PAGE
// ----------------------------------------------------
export default function DriverAppPage({ driverId = 1, serverUrl = '', onSwitchServerOrDriver = null, driverToken = '', tenantInfo = null }) {
  const baseUrl = (serverUrl || localStorage.getItem('loops_driver_server_url') || '').replace(/\/+$/, '');
  const activeToken = driverToken || localStorage.getItem('loops_driver_auth_token') || '';
  const tenantName = tenantInfo?.name || localStorage.getItem('loops_driver_tenant_name') || '';

  const apiFetch = async (url, options = {}) => {
    let effectiveBase = baseUrl;
    if ((!effectiveBase || effectiveBase.includes('localhost')) && Capacitor.isNativePlatform()) {
      effectiveBase = 'https://loops.thegreenfuture.ch';
    }
    const fullUrl = url.startsWith('http://') || url.startsWith('https://')
      ? url
      : `${effectiveBase}${url.startsWith('/') ? '' : '/'}${url}`;

    const headers = new Headers(options.headers || {});
    if (activeToken && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${activeToken}`);
    }
    const mergedOptions = { ...options, headers };

    const res = await fetch(fullUrl, mergedOptions);
    if (res.status === 401) {
      const clone = res.clone();
      const body = await clone.json().catch(() => ({}));
      if (body.code === 'DEVICE_REVOKED') {
        alert('Cet appareil a été révoqué par le gestionnaire de flotte.');
        localStorage.removeItem('loops_driver_auth_token');
        if (onSwitchServerOrDriver) onSwitchServerOrDriver();
      }
    }
    return res;
  };

  const handleDisconnect = () => {
    if (window.confirm("Voulez-vous vous déconnecter pour scanner un nouveau QR code ?")) {
      try {
        stopBackgroundTracking();
      } catch (err) {
        console.warn('Error stopping background tracking on disconnect:', err);
      }
      localStorage.removeItem('loops_driver_auth_token');
      localStorage.removeItem('loops_driver_id');
      localStorage.removeItem('loops_driver_tenant_id');
      localStorage.removeItem('loops_driver_tenant_name');
      localStorage.removeItem('loops_driver_profile');
      if (onSwitchServerOrDriver) {
        onSwitchServerOrDriver();
      }
    }
  };

  const resolvedBaseUrl = (baseUrl && !baseUrl.includes('localhost'))
    ? baseUrl
    : (Capacitor.isNativePlatform() ? 'https://loops.thegreenfuture.ch' : (typeof window !== 'undefined' ? window.location.origin : ''));

  const resolveAssetUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('data:') || url.startsWith('blob:') || url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    const cleanBase = (resolvedBaseUrl || '').replace(/\/+$/, '');
    const cleanPath = url.startsWith('/') ? url : `/${url}`;
    return cleanBase ? `${cleanBase}${cleanPath}` : cleanPath;
  };

  const handleTriggerPhotoCapture = async () => {
    if (Capacitor.isNativePlatform()) {
      try {
        const image = await CapCamera.getPhoto({
          quality: 85,
          allowEditing: false,
          resultType: CameraResultType.DataUrl,
          source: CameraSource.Prompt,
          promptLabelHeader: 'Pièce jointe photo',
          promptLabelPhoto: 'Choisir depuis la galerie',
          promptLabelPicture: 'Prendre une photo avec l\'appareil'
        });
        if (image?.dataUrl) {
          setPhotoDataUrl(image.dataUrl);
        }
      } catch (err) {
        console.warn('Native camera capture cancelled or failed:', err);
      }
    } else {
      fileInputRef.current?.click();
    }
  };

  const [driver, setDriver] = useState(null);
  const [trucks, setTrucks] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [stops, setStops] = useState([]);
  const [hubs, setHubs] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientContacts, setClientContacts] = useState([]);
  const [objects, setObjects] = useState([]);
  const [allJobAttachments, setAllJobAttachments] = useState([]);
  const [jobPauses, setJobPauses] = useState([]);
  
  // Date Selector State (defaults to current day)
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const selectedDateISO = formatDateISO(currentDate);

  const handlePrevDay = () => {
    setCurrentDate(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() - 1);
      return d;
    });
    setSelectedJobId(null);
  };

  const handleNextDay = () => {
    setCurrentDate(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() + 1);
      return d;
    });
    setSelectedJobId(null);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
    setSelectedJobId(null);
  };

  const [selectedJobId, setSelectedJobId] = useState(null);
  const [activeStopModal, setActiveStopModal] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'map'
  const [geohistory, setGeohistory] = useState([]);
  const [showGeohistoryDebugModal, setShowGeohistoryDebugModal] = useState(false);
  const [showClientInfoModal, setShowClientInfoModal] = useState(false);
  const [showPostitModal, setShowPostitModal] = useState(false);
  
  // Non-blocking Pause state
  const [isPaused, setIsPaused] = useState(false);
  const [pauseStartTime, setPauseStartTime] = useState(null);
  const [pauseElapsedSec, setPauseElapsedSec] = useState(0);
  
  // Stop popup form & sub-modal states
  const [driverNotes, setDriverNotes] = useState('');
  const [paymentType, setPaymentType] = useState('none');
  const [amountPaid, setAmountPaid] = useState('');
  const [stopAttachments, setStopAttachments] = useState([]);
  const [stopReturnTickets, setStopReturnTickets] = useState([]);
  
  // Sub-action modals ('action_menu' | 'photo' | 'driver_sig' | 'recipient_sig' | 'return_ticket' | 'problem_reasons')
  const [activeActionModal, setActiveActionModal] = useState(null);
  
  // Return ticket tabs ('returned' = consignes / retours, 'order_items' = articles commande)
  const [returnTicketTab, setReturnTicketTab] = useState('returned');
  
  // Problem reasons multi-select state
  const [selectedProblemReasons, setSelectedProblemReasons] = useState([]);
  const commonProblemReasons = [
    { id: 'absent', label: 'Client absent / Fermé', icon: '🚫' },
    { id: 'wrong_address', label: 'Adresse introuvable / Erronée', icon: '📍' },
    { id: 'refused', label: 'Refus de livraison par le destinataire', icon: '🛑' },
    { id: 'damaged', label: 'Colis ou emballage endommagé', icon: '📦' },
    { id: 'missing_items', label: 'Marchandise manquante / incomplète', icon: '⚠️' },
    { id: 'access_blocked', label: 'Accès impossible (travaux / barrière)', icon: '🚧' },
    { id: 'payment_missing', label: 'Paiement non disponible', icon: '💳' },
    { id: 'other', label: 'Autre motif', icon: '❓' }
  ];

  // Recipient signature state
  const [recipientContactId, setRecipientContactId] = useState('');
  const [recipientCustomLastName, setRecipientCustomLastName] = useState('');
  const [recipientCustomFirstName, setRecipientCustomFirstName] = useState('');

  // Return ticket state
  const [ticketOrderId, setTicketOrderId] = useState('');
  const [ticketLines, setTicketLines] = useState([]);
  const [allReturnTickets, setAllReturnTickets] = useState([]);
  const [showTruckContentModal, setShowTruckContentModal] = useState(false);
  const [truckTab, setTruckTab] = useState('totals'); // 'totals' | 'clients'
  const [editingTicketId, setEditingTicketId] = useState(null);
  const [editingTicketStopId, setEditingTicketStopId] = useState(null);

  // Photo upload state
  const [photoDataUrl, setPhotoDataUrl] = useState('');
  const [photoType, setPhotoType] = useState('other_photo');
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);

  // Client driver post-it state
  const [clientPostitText, setClientPostitText] = useState('');
  const [savingPostit, setSavingPostit] = useState(false);

  // Odometer Real Km state
  const [startKm, setStartKm] = useState('');
  const [endKm, setEndKm] = useState('');
  const [savedStartKmIndicator, setSavedStartKmIndicator] = useState(false);
  const [savedEndKmIndicator, setSavedEndKmIndicator] = useState(false);
  const isStartKmDirtyRef = useRef(false);
  const isEndKmDirtyRef = useRef(false);

  const handleSaveClientPostit = async (clientId) => {
    if (!clientId) return;
    setSavingPostit(true);
    try {
      const res = await apiFetch(`/api/clients/${clientId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ driver_postit: clientPostitText })
      });
      if (res.ok) {
        setClients(prev => prev.map(c => c.id === clientId ? { ...c, driver_postit: clientPostitText } : c));
        setStops(prev => prev.map(s => s.client_id === clientId ? { ...s, client_driver_postit: clientPostitText } : s));
        if (activeStopModal && activeStopModal.client_id === clientId) {
          setActiveStopModal(prev => ({ ...prev, client_driver_postit: clientPostitText }));
        }
      }
    } catch (err) {
      console.error('Error saving driver postit:', err);
    } finally {
      setSavingPostit(false);
    }
  };

  // Live timer for pause
  useEffect(() => {
    let interval = null;
    if (isPaused && pauseStartTime) {
      interval = setInterval(() => {
        const sec = Math.floor((Date.now() - pauseStartTime) / 1000);
        setPauseElapsedSec(sec);
      }, 1000);
    } else {
      setPauseElapsedSec(0);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isPaused, pauseStartTime]);

  // Unblock body scrolling for mobile & desktop Driver App
  useEffect(() => {
    const prevBodyOverflow = document.body.style.overflow;
    const prevBodyHeight = document.body.style.height;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevHtmlHeight = document.documentElement.style.height;

    document.body.style.overflow = 'auto';
    document.body.style.height = 'auto';
    document.documentElement.style.overflow = 'auto';
    document.documentElement.style.height = 'auto';

    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.body.style.height = prevBodyHeight;
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.documentElement.style.height = prevHtmlHeight;
    };
  }, []);

  const loadDriverData = async (targetDateStr = null) => {
    try {
      const dateStr = targetDateStr || selectedDateISO;
      const res = await apiFetch(`/api/bootstrap?date=${dateStr}&window_days=0`);
      const data = await res.json();
      const allDrivers = data.drivers || [];
      const currentDriver = allDrivers.find(d => d.id === parseInt(driverId)) || allDrivers[0] || {
        id: 1, first_name: 'Chauffeur', last_name: 'Inconnu', email: 'driver@loops.ch', phone: '+41 79 123 45 67', driving_license: 'B'
      };

      setDriver(currentDriver);
      setTrucks(data.trucks || []);
      setRounds(data.rounds || []);
      setJobs(data.jobs || []);
      setStops(data.stops || []);
      setHubs(data.hubs || []);
      setClients(data.clients || []);
      setClientContacts(data.client_contacts || []);
      setObjects(data.objects || []);
      setAllJobAttachments(data.stop_attachments || []);
      setAllReturnTickets(data.return_tickets || []);
      setJobPauses(data.job_pauses || []);
    } catch (err) {
      console.error('Error fetching driver app bootstrap data:', err);
    }
  };

  // Reload data whenever date or driver changes
  useEffect(() => {
    loadDriverData(selectedDateISO);
  }, [selectedDateISO, driverId]);

  // Robust WebSocket Connection with auto-reconnect & cross-event support
  useEffect(() => {
    let ws = null;
    let reconnectTimeout = null;
    let isUnmounted = false;

    const getWsUrl = () => {
      if (baseUrl) {
        try {
          const u = new URL(baseUrl);
          const protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
          return `${protocol}//${u.host}`;
        } catch (e) {}
      }
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.hostname;
      const port = (window.location.port === '3000' || window.location.port === '5173') ? '3001' : (window.location.port || '5000');
      return `${protocol}//${host}:${port}`;
    };

    const connectWs = () => {
      if (isUnmounted) return;
      try {
        ws = new WebSocket(getWsUrl());

        ws.onopen = () => {
          if (!isUnmounted) setWsConnected(true);
        };

        ws.onmessage = (event) => {
          try {
            const payload = JSON.parse(event.data);
            if (payload.type === 'REALTIME_DRIVER_UPDATE') {
              if (payload.event_type === 'DRIVER_LOCATION_UPDATE') {
                const pt = {
                  id: payload.id,
                  driver_id: payload.driver_id,
                  job_id: payload.job_id,
                  latitude: parseFloat(payload.latitude),
                  longitude: parseFloat(payload.longitude),
                  speed: payload.speed !== null && payload.speed !== undefined ? parseFloat(payload.speed) : null,
                  heading: payload.heading !== null && payload.heading !== undefined ? parseFloat(payload.heading) : null,
                  accuracy: payload.accuracy !== null && payload.accuracy !== undefined ? parseFloat(payload.accuracy) : null,
                  recorded_at: payload.recorded_at
                };
                if (!isNaN(pt.latitude) && !isNaN(pt.longitude)) {
                  setGeohistory(prev => {
                    const exists = prev.some(p => p.id && pt.id && p.id === pt.id);
                    return exists ? prev : [...prev, pt];
                  });
                }
                return;
              }

              const { stop, job } = payload;
              if (stop) {
                setStops(prev => prev.map(s => s.id === stop.id ? { ...s, ...stop } : s));
                setActiveStopModal(prev => prev && prev.id === stop.id ? { ...prev, ...stop } : prev);
              }
              if (job) {
                setJobs(prev => prev.map(j => j.id === job.id ? { ...j, ...job } : j));
              }
            }
            loadDriverData(selectedDateISO);
          } catch (err) {
            console.error('Error parsing WS message:', err);
          }
        };

        ws.onclose = () => {
          if (!isUnmounted) {
            setWsConnected(false);
            reconnectTimeout = setTimeout(connectWs, 3000);
          }
        };

        ws.onerror = () => {
          if (!isUnmounted) {
            setWsConnected(false);
            try { ws.close(); } catch(e){}
          }
        };
      } catch (e) {
        if (!isUnmounted) {
          reconnectTimeout = setTimeout(connectWs, 3000);
        }
      }
    };

    connectWs();

    return () => {
      isUnmounted = true;
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (ws) ws.close();
    };
  }, [driverId, selectedDateISO]);

  const isStopOnSelectedDate = (stop) => {
    const d = selectedDateISO;
    if (!d) return true;

    if (stop.delivery_date) {
      const stopDateStr = String(stop.delivery_date).slice(0, 10);
      return stopDateStr === d;
    }
    if (stop.actual_arrival && String(stop.actual_arrival).startsWith(d)) return true;
    if (stop.estimated_arrival && String(stop.estimated_arrival).startsWith(d)) return true;
    if (stop.real_departure && String(stop.real_departure).startsWith(d)) return true;
    return false;
  };

  const isJobOnSelectedDate = (job) => {
    const d = selectedDateISO;
    if (!d) return true;

    const jobStops = stops.filter(s => s.job_id === job.id);
    if (jobStops.length > 0) {
      return jobStops.some(s => isStopOnSelectedDate(s));
    }
    if (job.real_start && String(job.real_start).startsWith(d)) return true;
    if (job.real_end && String(job.real_end).startsWith(d)) return true;
    if (job.created_at && String(job.created_at).slice(0, 10) === d) return true;
    return false;
  };

  const driverJobs = jobs
    .filter(j => j.driver_id === parseInt(driver?.id || 1))
    .filter(isJobOnSelectedDate);
  const currentJob = jobs.find(j => j.id === selectedJobId) || null;

  // Sync real_start_km and real_end_km from loaded job (avoiding overwriting if user is typing)
  useEffect(() => {
    if (currentJob) {
      if (!isStartKmDirtyRef.current) {
        setStartKm(currentJob.real_start_km !== null && currentJob.real_start_km !== undefined ? String(currentJob.real_start_km) : '');
      }
      if (!isEndKmDirtyRef.current) {
        setEndKm(currentJob.real_end_km !== null && currentJob.real_end_km !== undefined ? String(currentJob.real_end_km) : '');
      }
    }
  }, [currentJob?.id, currentJob?.real_start_km, currentJob?.real_end_km]);

  // Debounced auto-save for startKm and endKm
  useEffect(() => {
    if (!isStartKmDirtyRef.current) return;
    const timer = setTimeout(() => {
      isStartKmDirtyRef.current = false;
      handleUpdateJobKm('real_start_km', startKm);
    }, 800);
    return () => clearTimeout(timer);
  }, [startKm]);

  useEffect(() => {
    if (!isEndKmDirtyRef.current) return;
    const timer = setTimeout(() => {
      isEndKmDirtyRef.current = false;
      handleUpdateJobKm('real_end_km', endKm);
    }, 800);
    return () => clearTimeout(timer);
  }, [endKm]);

  // Fetch geohistory whenever a job is selected
  const fetchGeohistory = async (jobId) => {
    if (!jobId) return;
    try {
      const res = await apiFetch(`/api/driver-geohistory?job_id=${jobId}`);
      if (res.ok) {
        const data = await res.json();
        setGeohistory(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error fetching driver geohistory:', err);
    }
  };

  useEffect(() => {
    if (selectedJobId) {
      fetchGeohistory(selectedJobId);
    } else {
      setGeohistory([]);
    }
  }, [selectedJobId]);

  // Automatic Geolocation & Permanent Background Tracking (Capacitor Foreground Service & Web Fallback)
  useEffect(() => {
    if (!currentJob || currentJob.status !== 'in_progress') {
      stopBackgroundTracking();
      return;
    }

    startBackgroundTracking({
      jobId: currentJob.id,
      driverId: driver?.id || parseInt(driverId) || null,
      truckCode: currentJob.truck_code,
      serverUrl: resolvedBaseUrl,
      authToken: activeToken,
      onLocationUpdate: (entry) => {
        if (!entry) return;
        setGeohistory(prev => {
          const exists = prev.some(p => p.id && entry.id && p.id === entry.id);
          return exists ? prev : [...prev, entry];
        });
      }
    });

    return () => {
      // Cleanup happens when currentJob.status changes or component unmounts
    };
  }, [currentJob?.id, currentJob?.status, driver?.id, driverId, resolvedBaseUrl, activeToken]);

  const isJobStarted = currentJob && (currentJob.status === 'in_progress' || currentJob.status === 'completed' || currentJob.status === 'done');

  // Job stops sorted sequentially by sequence_order (backend ensures clean 1..N order: completed first, then pending)
  const currentJobStops = currentJob
    ? stops.filter(s => s.job_id === currentJob.id).sort((a, b) => {
        const seqA = a.sequence_order !== null && a.sequence_order !== undefined ? parseInt(a.sequence_order) : (a.estimated_index || 0);
        const seqB = b.sequence_order !== null && b.sequence_order !== undefined ? parseInt(b.sequence_order) : (b.estimated_index || 0);
        return seqA - seqB;
      })
    : [];

  const allJobStopsValidated = currentJobStops.length > 0 && currentJobStops.every(s => s.status === 'delivered' || s.status === 'failed');

  // Find first stop with driver signature in this mission
  const firstStopWithDriverSig = currentJobStops.find(s => {
    const att = allJobAttachments.find(a => a.stop_id === s.id && a.attachment_type === 'driver_signature');
    return !!att;
  });
  const firstStopSigUrl = firstStopWithDriverSig
    ? allJobAttachments.find(a => a.stop_id === firstStopWithDriverSig.id && a.attachment_type === 'driver_signature')?.value
    : null;

  // Find if current active stop's client has another stop in this job with an existing client signature
  const otherStopSameClient = activeStopModal
    ? currentJobStops.find(s => {
        if (s.id === activeStopModal.id) return false;
        if (s.client_id !== activeStopModal.client_id) return false;
        return allJobAttachments.some(a => a.stop_id === s.id && a.attachment_type === 'recipient_signature');
      })
    : null;

  const previousClientSigAtt = otherStopSameClient
    ? allJobAttachments.find(a => a.stop_id === otherStopSameClient.id && a.attachment_type === 'recipient_signature')
    : null;
  const previousClientSigUrl = previousClientSigAtt?.value || null;

  const previousClientNameAtt = otherStopSameClient
    ? allJobAttachments.find(a => a.stop_id === otherStopSameClient.id && a.attachment_type === 'recipient_signature_name')
    : null;
  const previousClientSignerName = previousClientNameAtt?.value || '';

  // Daily driver stats
  const totalDriverKm = driverJobs.reduce((acc, j) => acc + parseFloat(j.estimated_length_km || j.real_length_km || 0), 0);
  const allDriverStops = stops.filter(s => driverJobs.some(j => j.id === s.job_id) && isStopOnSelectedDate(s));
  const completedDriverStops = allDriverStops.filter(s => s.status === 'delivered' || s.status === 'failed');
  const totalDriverDurationSec = driverJobs.reduce((acc, j) => acc + parseInt(j.estimated_duration_sec || j.real_duration_sec || 0), 0);
  const hours = Math.floor(totalDriverDurationSec / 3600);
  const mins = Math.floor((totalDriverDurationSec % 3600) / 60);

  const handleUpdateJobKm = async (field, value) => {
    if (!currentJob) return;
    const numVal = value !== '' && !isNaN(value) ? parseFloat(value) : null;
    try {
      await apiFetch(`/api/jobs/${currentJob.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: numVal, recalculate_metrics: true })
      });
      if (field === 'real_start_km') {
        setSavedStartKmIndicator(true);
        setTimeout(() => setSavedStartKmIndicator(false), 2000);
      } else if (field === 'real_end_km') {
        setSavedEndKmIndicator(true);
        setTimeout(() => setSavedEndKmIndicator(false), 2000);
      }
      await loadDriverData();
    } catch (err) {
      console.error(`Error updating job ${field}:`, err);
    }
  };

  const handleStartMission = async () => {
    if (!currentJob) return;
    setLoading(true);
    try {
      const res = await apiFetch(`/api/jobs/${currentJob.id}/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          real_start_km: startKm !== '' ? parseFloat(startKm) : null,
          real_start: new Date().toISOString()
        })
      });
      if (res.ok) {
        startBackgroundTracking({
          jobId: currentJob.id,
          driverId: driver?.id || parseInt(driverId) || null,
          truckCode: currentJob.truck_code,
          serverUrl: resolvedBaseUrl,
          authToken: activeToken,
          onLocationUpdate: (entry) => {
            if (!entry) return;
            setGeohistory(prev => {
              const exists = prev.some(p => p.id && entry.id && p.id === entry.id);
              return exists ? prev : [...prev, entry];
            });
          }
        });
        await loadDriverData();
      }
    } catch (err) {
      console.error('Error starting mission:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFinishMission = async () => {
    if (!currentJob) return;
    setLoading(true);
    try {
      const res = await apiFetch(`/api/jobs/${currentJob.id}/finish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          real_start_km: startKm !== '' ? parseFloat(startKm) : null,
          real_end_km: endKm !== '' ? parseFloat(endKm) : null
        })
      });
      if (res.ok) {
        stopBackgroundTracking();
        await loadDriverData();
      }
    } catch (err) {
      console.error('Error finishing mission:', err);
    } finally {
      setLoading(false);
    }
  };

  // Pause Controls
  const handleTogglePause = async () => {
    if (!isPaused) {
      // Start pause
      setIsPaused(true);
      setPauseStartTime(Date.now());
      setPauseElapsedSec(0);
    } else {
      // Resume / complete pause and log real pause
      const durationMins = Math.max(1, Math.round(pauseElapsedSec / 60));
      const firstPendingStop = currentJobStops.find(s => s.status === 'pending');
      
      setLoading(true);
      try {
        const now = new Date();
        const pStart = pauseStartTime ? new Date(pauseStartTime) : new Date(Date.now() - durationMins * 60000);
        const startStr = formatLocalTimeHHMM(pStart);
        const endStr = formatLocalTimeHHMM(now);

        await apiFetch('/api/job-pauses', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            job_id: currentJob.id,
            before_stop_id: firstPendingStop?.id || null,
            type: 'real',
            start: startStr,
            end: endStr,
            duration_mins: durationMins,
            pause_label: `Pause réelle (${durationMins} min)`
          })
        });
        await loadDriverData();
      } catch (e) {
        console.error('Error saving real pause:', e);
      } finally {
        setIsPaused(false);
        setPauseStartTime(null);
        setPauseElapsedSec(0);
        setLoading(false);
      }
    }
  };

  // Open Stop Popup and load attachments & return tickets
  const handleOpenStopModal = async (stop) => {
    setActiveStopModal(stop);
    const client = clients.find(c => c.id === stop.client_id);
    setClientPostitText(client?.driver_postit || stop.client_driver_postit || '');
    setDriverNotes(stop.driver_notes || '');
    setPaymentType(stop.payment_type || 'none');
    setAmountPaid(stop.amount_paid !== undefined && stop.amount_paid !== null ? String(stop.amount_paid) : '');
    setSelectedProblemReasons(stop.problem_reasons ? stop.problem_reasons.split(', ') : []);
    setActiveActionModal(null);
    setShowClientInfoModal(false);
    setShowPostitModal(false);
    setRecipientCustomLastName('');
    setRecipientCustomFirstName('');
    setStopAttachments([]);
    setStopReturnTickets([]);

    try {
      const [attRes, tRes] = await Promise.all([
        apiFetch(`/api/stops/${stop.id}/attachments`),
        apiFetch(`/api/stops/${stop.id}/return-tickets`)
      ]);
      if (attRes.ok) {
        const attData = await attRes.json();
        setStopAttachments(attData);
      }
      if (tRes.ok) {
        const tData = await tRes.json();
        setStopReturnTickets(tData);
      }
    } catch (e) {
      console.error('Error loading stop details:', e);
    }
  };

  const handleMarkArrival = async (stopId) => {
    if (!currentJob || currentJob.status !== 'in_progress') {
      alert("Veuillez d'abord démarrer la tournée pour pouvoir marquer votre arrivée.");
      return;
    }
    setLoading(true);
    try {
      const res = await apiFetch(`/api/stops/${stopId}/arrival`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        if (data.stop) {
          setStops(prev => prev.map(s => s.id === data.stop.id ? { ...s, ...data.stop } : s));
          setActiveStopModal(prev => prev && prev.id === data.stop.id ? { ...prev, ...data.stop } : prev);
        }
        await loadDriverData();
      }
    } catch (err) {
      console.error('Error marking arrival:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteStop = async (stopId, status, customNotes = null, customReasons = null) => {
    if (!currentJob || currentJob.status !== 'in_progress') {
      alert("Veuillez d'abord démarrer la tournée pour pouvoir valider ce stop.");
      return;
    }
    setLoading(true);
    try {
      const res = await apiFetch(`/api/stops/${stopId}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status,
          driver_notes: customNotes !== null ? customNotes : driverNotes,
          payment_type: paymentType,
          amount_paid: amountPaid ? parseFloat(amountPaid) : 0.00,
          problem_reasons: customReasons !== null ? customReasons : selectedProblemReasons
        })
      });
      if (res.ok) {
        await loadDriverData();
        setDriverNotes('');
        setActiveStopModal(null);
        setActiveActionModal(null);
      }
    } catch (err) {
      console.error('Error completing stop:', err);
    } finally {
      setLoading(false);
    }
  };

  // Upload handler for photos & signatures
  const handleUploadAndAttach = async (base64Data, type, filename = 'attachment') => {
    if (!activeStopModal) return;
    setLoading(true);
    try {
      const folder = type.includes('signature') ? 'signatures' : 'photos';
      const uploadRes = await apiFetch('/api/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: base64Data, filename, folder })
      });
      if (uploadRes.ok) {
        const uploadData = await uploadRes.json();
        const attRes = await apiFetch(`/api/stops/${activeStopModal.id}/attachments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attachment_type: type, value: uploadData.url })
        });
        if (attRes.ok) {
          const newAtt = await attRes.json();
          setStopAttachments(prev => [...prev, newAtt]);
          setAllJobAttachments(prev => [...prev, newAtt]);
        }
      }
    } catch (err) {
      console.error('Error uploading attachment:', err);
    } finally {
      setLoading(false);
      setActiveActionModal(null);
    }
  };

  // Copy driver signature from first stop
  const handleCopyFirstStopSignature = async () => {
    if (!firstStopSigUrl || !activeStopModal) return;
    setLoading(true);
    try {
      const attRes = await apiFetch(`/api/stops/${activeStopModal.id}/attachments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attachment_type: 'driver_signature', value: firstStopSigUrl, duplicate_file: false })
      });
      if (attRes.ok) {
        const newAtt = await attRes.json();
        setStopAttachments(prev => [...prev.filter(a => a.attachment_type !== 'driver_signature'), newAtt]);
        setAllJobAttachments(prev => [...prev.filter(a => a.stop_id !== activeStopModal.id || a.attachment_type !== 'driver_signature'), newAtt]);
        setActiveActionModal(null);
      }
    } catch (err) {
      console.error('Error copying driver signature:', err);
    } finally {
      setLoading(false);
    }
  };

  // Copy client signature and signer name from another stop of the same client in this job
  const handleCopyClientSignature = async () => {
    if (!previousClientSigUrl || !activeStopModal) return;
    setLoading(true);
    try {
      const sigRes = await apiFetch(`/api/stops/${activeStopModal.id}/attachments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attachment_type: 'recipient_signature',
          value: previousClientSigUrl,
          duplicate_file: true
        })
      });

      let newNameAtt = null;
      if (previousClientSignerName) {
        const nameRes = await apiFetch(`/api/stops/${activeStopModal.id}/attachments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attachment_type: 'recipient_signature_name',
            value: previousClientSignerName
          })
        });
        if (nameRes.ok) {
          newNameAtt = await nameRes.json();
        }
      }

      if (sigRes.ok) {
        const newSigAtt = await sigRes.json();
        setStopAttachments(prev => [
          ...prev.filter(a => a.attachment_type !== 'recipient_signature' && a.attachment_type !== 'recipient_signature_name'),
          newSigAtt,
          ...(newNameAtt ? [newNameAtt] : [])
        ]);
        setAllJobAttachments(prev => [
          ...prev.filter(a => a.stop_id !== activeStopModal.id || (a.attachment_type !== 'recipient_signature' && a.attachment_type !== 'recipient_signature_name')),
          newSigAtt,
          ...(newNameAtt ? [newNameAtt] : [])
        ]);
        setActiveActionModal(null);
      }
    } catch (err) {
      console.error('Error copying client signature:', err);
    } finally {
      setLoading(false);
    }
  };

  // Delete attachment
  const handleDeleteAttachment = async (attachmentId) => {
    try {
      const res = await apiFetch(`/api/attachments/${attachmentId}`, { method: 'DELETE' });
      if (res.ok) {
        setStopAttachments(prev => prev.filter(a => a.id !== attachmentId));
        setAllJobAttachments(prev => prev.filter(a => a.id !== attachmentId));
      }
    } catch (err) {
      console.error('Error deleting attachment:', err);
    }
  };

  // Delete recipient signature and its accompanying signer name
  const handleDeleteRecipientSignature = async (sigAttId, nameAttId) => {
    try {
      if (sigAttId) await handleDeleteAttachment(sigAttId);
      if (nameAttId) await handleDeleteAttachment(nameAttId);
    } catch (err) {
      console.error('Error deleting recipient signature and name:', err);
    }
  };

  // Open modal to edit an existing return ticket
  const handleOpenEditReturnTicket = (ticket) => {
    const parentStop = stops.find(s => s.id === ticket.stop_id);
    if (parentStop && !activeStopModal) {
      setActiveStopModal(parentStop);
    }
    setEditingTicketId(ticket.id);
    setEditingTicketStopId(ticket.stop_id);
    setTicketOrderId(ticket.order_id || '');
    setReturnTicketTab('returned');

    const existingMap = new Map();
    if (ticket.lines) {
      ticket.lines.forEach(l => existingMap.set(l.object_id, l));
    }
    const initialLines = objects.map(o => {
      const match = existingMap.get(o.id);
      return {
        object_id: o.id,
        amount: match ? match.amount : 0,
        unit_price: match ? parseFloat(match.unit_price || o.price || 0) : parseFloat(o.price || 0)
      };
    });
    setTicketLines(initialLines);
    setActiveActionModal('return_ticket');
  };

  // Delete a return ticket
  const handleDeleteReturnTicket = async (ticketId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer définitivement ce bon de retour ?')) {
      return;
    }
    setLoading(true);
    try {
      const res = await apiFetch(`/api/return-tickets/${ticketId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        setStopReturnTickets(prev => prev.filter(t => t.id !== ticketId));
        setAllReturnTickets(prev => prev.filter(t => t.id !== ticketId));
      }
    } catch (err) {
      console.error('Error deleting return ticket:', err);
    } finally {
      setLoading(false);
    }
  };

  // Create or Update validated return ticket
  const handleSaveReturnTicket = async () => {
    const targetStopId = activeStopModal?.id || editingTicketStopId;
    if (!targetStopId) return;
    const activeLines = ticketLines.filter(l => (l.amount || 0) !== 0);
    if (activeLines.length === 0) {
      alert('Veuillez renseigner au moins une quantité pour un objet ou article.');
      return;
    }
    setLoading(true);
    try {
      if (editingTicketId) {
        const res = await apiFetch(`/api/return-tickets/${editingTicketId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            order_id: ticketOrderId || null,
            lines: activeLines
          })
        });
        if (res.ok) {
          const updated = await res.json();
          setStopReturnTickets(prev => prev.map(t => t.id === updated.id ? updated : t));
          setAllReturnTickets(prev => prev.map(t => t.id === updated.id ? updated : t));
          setTicketLines([]);
          setTicketOrderId('');
          setEditingTicketId(null);
          setEditingTicketStopId(null);
          setActiveActionModal(null);
        }
      } else {
        const res = await apiFetch(`/api/stops/${targetStopId}/return-tickets`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            order_id: ticketOrderId || null,
            lines: activeLines
          })
        });
        if (res.ok) {
          const created = await res.json();
          setStopReturnTickets(prev => [...prev, created]);
          setAllReturnTickets(prev => [...prev.filter(t => t.id !== created.id), created]);
          setTicketLines([]);
          setTicketOrderId('');
          setEditingTicketId(null);
          setEditingTicketStopId(null);
          setActiveActionModal(null);
        }
      }
    } catch (err) {
      console.error('Error saving return ticket:', err);
    } finally {
      setLoading(false);
    }
  };

  // Return tickets belonging to the current selected job's stops
  const jobReturnTickets = useMemo(() => {
    if (!currentJob || currentJobStops.length === 0) return [];
    const stopIdSet = new Set(currentJobStops.map(s => s.id));
    return allReturnTickets.filter(t => stopIdSet.has(t.stop_id));
  }, [currentJob, currentJobStops, allReturnTickets]);

  // Aggregated truck returned totals
  const truckReturnedTotals = useMemo(() => {
    const objectMap = {};
    let totalItemsCount = 0;
    let totalValue = 0;

    for (const ticket of jobReturnTickets) {
      if (!ticket.lines) continue;
      const stop = currentJobStops.find(s => s.id === ticket.stop_id);
      const client = clients.find(c => c.id === stop?.client_id);
      const stopIdx = currentJobStops.findIndex(s => s.id === ticket.stop_id);
      const stopNum = stop?.real_index || stop?.estimated_index || (stopIdx >= 0 ? stopIdx + 1 : ticket.stop_id);
      const stopLabel = `Stop #${stopNum} (${client?.name || stop?.client_name || 'Client'})`;

      for (const line of ticket.lines) {
        const objId = line.object_id;
        const amt = parseInt(line.amount || 0);
        const uPrice = parseFloat(line.unit_price || 0);
        const lTotal = parseFloat(line.total_price !== undefined ? line.total_price : (amt * uPrice));

        if (!objectMap[objId]) {
          const objDef = objects.find(o => o.id === objId);
          objectMap[objId] = {
            object_id: objId,
            name: line.object_name || objDef?.name || 'Objet',
            unit_price: uPrice || parseFloat(objDef?.price || 0),
            weight_per_unit: parseFloat(objDef?.weight_per_unit || 0),
            volume_per_unit: parseFloat(objDef?.volume_per_unit || 0),
            is_returned: objDef?.is_returned ?? true,
            total_amount: 0,
            total_price: 0,
            client_breakdown: []
          };
        }

        objectMap[objId].total_amount += amt;
        objectMap[objId].total_price += lTotal;
        if (amt !== 0) {
          objectMap[objId].client_breakdown.push({
            stop_id: ticket.stop_id,
            stop_label: stopLabel,
            amount: amt,
            ticket_id: ticket.id
          });
        }

        if (amt > 0) totalItemsCount += amt;
        totalValue += lTotal;
      }
    }

    const items = Object.values(objectMap);
    items.sort((a, b) => b.total_amount - a.total_amount);

    return {
      items,
      totalItemsCount,
      totalValue
    };
  }, [jobReturnTickets, currentJobStops, clients, objects]);

  return (
    <div style={{ background: '#090d16', minHeight: '100vh', width: '100%', color: '#f8fafc', fontFamily: 'Inter, system-ui, sans-serif', display: 'flex', justifyContent: 'center', padding: '0 0 60px 0' }}>
      <div style={{ width: '100%', maxWidth: '540px', display: 'flex', flexDirection: 'column', background: '#0f172a', minHeight: '100vh', boxShadow: '0 0 40px rgba(0,0,0,0.6)' }}>
        
        {/* DRIVER INFO HEADER & DATE SELECTOR (ONLY WHEN NO MISSION SELECTED) */}
        {!selectedJobId && (
          <>
            {/* DRIVER INFO HEADER */}
            <div style={{ background: '#1e293b', borderBottom: '1px solid #334155', padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#38bdf8', color: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '900', fontSize: '1.1rem' }}>
                  {driver?.first_name ? driver.first_name[0] : 'C'}
                </div>
                <div>
                  <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '800', color: '#ffffff' }}>
                    {driver?.first_name} {driver?.last_name}
                  </h2>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '0.75rem', color: wsConnected ? '#22c55e' : '#f59e0b', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: wsConnected ? '#22c55e' : '#f59e0b' }}></span>
                  {wsConnected ? 'En ligne' : 'Reconnexion...'}
                </span>

                <button
                  type="button"
                  onClick={handleDisconnect}
                  title="Se déconnecter / Scanner un autre QR code"
                  style={{
                    background: 'rgba(239, 68, 68, 0.12)',
                    border: '1px solid rgba(239, 68, 68, 0.35)',
                    color: '#f87171',
                    borderRadius: '8px',
                    padding: '5px 10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    fontSize: '0.75rem',
                    fontWeight: '700',
                    cursor: 'pointer'
                  }}
                >
                  <LogOut size={13} />
                  <span>Changer</span>
                </button>
              </div>
            </div>

            {/* DATE SELECTOR BAR */}
            <div style={{
              background: '#131d31',
              borderBottom: '1px solid #334155',
              padding: '10px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              position: 'relative'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                background: '#0f172a',
                border: '1px solid #334155',
                borderRadius: '8px',
                padding: '4px 8px',
                gap: '8px',
                flex: 1,
                justifyContent: 'space-between',
                position: 'relative'
              }}>
                <button
                  type="button"
                  onClick={handlePrevDay}
                  style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}
                  title="Jour précédent"
                >
                  <ChevronLeft size={16} />
                </button>

                <div
                  onClick={() => setShowDatePicker(!showDatePicker)}
                  style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontWeight: '800',
                    fontSize: '0.86rem',
                    color: '#ffffff',
                    userSelect: 'none'
                  }}
                  title="Cliquer pour choisir une date"
                >
                  <Calendar size={14} color="#38bdf8" />
                  <span>{formatDateFormatted(currentDate)}</span>
                </div>

                <button
                  type="button"
                  onClick={handleNextDay}
                  style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}
                  title="Jour suivant"
                >
                  <ChevronRight size={16} />
                </button>

                {showDatePicker && (
                  <input
                    type="date"
                    value={formatDateISO(currentDate)}
                    onChange={(e) => {
                      if (e.target.value) {
                        const parts = e.target.value.split('-');
                        setCurrentDate(new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2])));
                        setSelectedJobId(null);
                      }
                      setShowDatePicker(false);
                    }}
                    onBlur={() => setShowDatePicker(false)}
                    autoFocus
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      marginTop: '6px',
                      background: '#1e293b',
                      color: '#ffffff',
                      border: '1px solid #38bdf8',
                      borderRadius: '8px',
                      padding: '6px 10px',
                      fontSize: '0.84rem',
                      zIndex: 1000,
                      outline: 'none',
                      boxShadow: '0 10px 25px rgba(0,0,0,0.5)'
                    }}
                  />
                )}
              </div>

              {formatDateISO(currentDate) !== formatDateISO(new Date()) && (
                <button
                  type="button"
                  onClick={handleToday}
                  style={{
                    marginLeft: '10px',
                    background: 'rgba(56, 189, 248, 0.15)',
                    border: '1px solid #38bdf8',
                    color: '#38bdf8',
                    borderRadius: '6px',
                    padding: '6px 10px',
                    fontSize: '0.72rem',
                    fontWeight: '800',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Aujourd'hui
                </button>
              )}
            </div>
          </>
        )}

        {/* NON-BLOCKING PERSISTENT PAUSE BANNER */}
        {isPaused && (
          <div style={{
            background: 'linear-gradient(90deg, #f59e0b, #d97706)',
            color: '#0f172a',
            padding: '10px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 4px 12px rgba(245, 158, 11, 0.35)',
            position: 'sticky',
            top: 0,
            zIndex: 1000
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Pause size={18} fill="#0f172a" />
              <div>
                <b style={{ fontSize: '0.84rem' }}>Pause en cours :</b>
                <span style={{ fontSize: '0.9rem', fontWeight: '900', marginLeft: '6px', fontFamily: 'monospace' }}>
                  {formatTimerHHMMSS(pauseElapsedSec)}
                </span>
              </div>
            </div>

            <button
              onClick={handleTogglePause}
              style={{
                background: '#0f172a',
                color: '#ffffff',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '6px',
                fontWeight: '900',
                fontSize: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                cursor: 'pointer'
              }}
            >
              <PlayCircle size={14} color="#22c55e" />
              <span>Reprendre</span>
            </button>
          </div>
        )}

        {/* HOME PAGE: LIST OF ASSIGNED JOBS */}
        {!selectedJobId && (
          <div style={{ flex: 1, padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            
            {/* DAILY STATS GRID */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', textAlign: 'center' }}>
              <div style={{ background: '#1e293b', padding: '10px 4px', borderRadius: '8px', border: '1px solid #334155' }}>
                <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>Missions</div>
                <div style={{ fontSize: '1rem', fontWeight: '900', color: '#ffffff', marginTop: '2px' }}>{driverJobs.length}</div>
              </div>

              <div style={{ background: '#1e293b', padding: '10px 4px', borderRadius: '8px', border: '1px solid #334155' }}>
                <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>Stops Livrés</div>
                <div style={{ fontSize: '1rem', fontWeight: '900', color: '#38bdf8', marginTop: '2px' }}>{completedDriverStops.length}/{allDriverStops.length}</div>
              </div>

              <div style={{ background: '#1e293b', padding: '10px 4px', borderRadius: '8px', border: '1px solid #334155' }}>
                <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>Distance Est.</div>
                <div style={{ fontSize: '1rem', fontWeight: '900', color: '#22c55e', marginTop: '2px' }}>{totalDriverKm.toFixed(1)} km</div>
              </div>

              <div style={{ background: '#1e293b', padding: '10px 4px', borderRadius: '8px', border: '1px solid #334155' }}>
                <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>Durée Est.</div>
                <div style={{ fontSize: '1rem', fontWeight: '900', color: '#f59e0b', marginTop: '2px' }}>{hours}h {mins}m</div>
              </div>
            </div>

            {/* JOBS LIST */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {driverJobs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b', background: '#1e293b', borderRadius: '12px', border: '1px solid #334155' }}>
                  <Truck size={36} color="#475569" style={{ marginBottom: '8px' }} />
                  <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#94a3b8' }}>Aucune tournée pour le {formatDateFormatted(currentDate)}</div>
                </div>
              ) : (
                driverJobs.map(job => {
                  const jobStops = stops.filter(s => s.job_id === job.id);
                  const round = rounds.find(r => r.id === job.round_id);
                  const truck = trucks.find(t => t.id === job.truck_id);
                  const startHub = hubs.find(h => h.id === job.start_hub_id) || hubs[0] || { name: 'Dépôt Principal' };
                  const endHub = hubs.find(h => h.id === job.end_hub_id) || startHub;
                  const payloadStats = calculateTruckPeakPayload(truck, jobStops);

                  const displayKm = parseFloat(job.estimated_length_km || 0);
                  const displaySec = parseInt(job.estimated_duration_sec || 0);
                  const calculatedReturnTime = calculateReturnTime(job.estimated_departure || '07:30', displaySec);

                  let statusHeaderBg = '#1e293b';
                  let statusText = 'À faire';
                  let statusColor = '#f59e0b';
                  if (job.status === 'in_progress') {
                    statusHeaderBg = 'rgba(56, 189, 248, 0.25)';
                    statusText = 'En cours';
                    statusColor = '#38bdf8';
                  } else if (job.status === 'completed' || job.status === 'done') {
                    statusHeaderBg = 'rgba(34, 197, 94, 0.25)';
                    statusText = 'Clôturée';
                    statusColor = '#22c55e';
                  }

                  return (
                    <div
                      key={job.id}
                      onClick={() => { setSelectedJobId(job.id); setViewMode('list'); }}
                      style={{
                        background: '#1e293b',
                        border: `1px solid ${job.status === 'in_progress' ? '#38bdf8' : '#334155'}`,
                        borderRadius: '12px',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      <div
                        style={{
                          background: statusHeaderBg,
                          borderLeft: `5px solid ${truck?.color || '#38bdf8'}`,
                          padding: '12px 14px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          borderBottom: '1px solid #334155'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span style={{ fontWeight: '800', fontSize: '1rem', color: '#ffffff' }}>
                            {job.display_name}
                          </span>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '0.72rem', fontWeight: '800', color: statusColor, background: '#0f172a', padding: '2px 8px', borderRadius: '6px', border: '1px solid #334155' }}>
                            {statusText}
                          </span>
                          <ChevronRight size={16} color="#94a3b8" />
                        </div>
                      </div>

                      <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {/* VEHICLE & PAYLOAD ROW */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.75rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                            <Truck size={14} color={truck?.color || '#38bdf8'} />
                            <span style={{ color: '#ffffff', fontWeight: '600' }}>{truck?.plate || truck?.name || 'Véhicule standard'}</span>
                            <div
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontSize: '0.68rem',
                                fontWeight: '500',
                                background: payloadStats.isOverweight || payloadStats.isOvervolume ? 'rgba(239, 68, 68, 0.25)' : '#0f172a',
                                border: payloadStats.isOverweight || payloadStats.isOvervolume ? '1px solid #ef4444' : '1px solid #334155',
                                padding: '1px 6px',
                                borderRadius: '4px',
                                marginLeft: '2px'
                              }}
                              title={`Charge crête : ${payloadStats.peakWeight} / ${payloadStats.maxWeight} kg | ${payloadStats.peakVolume} / ${payloadStats.maxVolume} m³`}
                            >
                              <span style={{ color: payloadStats.isOverweight ? '#ef4444' : '#cbd5e1' }}>
                                {formatWeightPair(payloadStats.peakWeight, payloadStats.maxWeight)}
                              </span>
                              <span style={{ color: '#64748b' }}>|</span>
                              <span style={{ color: payloadStats.isOvervolume ? '#ef4444' : '#cbd5e1' }}>
                                {payloadStats.peakVolume}/{payloadStats.maxVolume}m³
                              </span>
                            </div>
                          </div>
                          <span style={{ color: '#38bdf8', fontWeight: '700' }}>{jobStops.length} stops</span>
                        </div>

                        {/* STATS METRICS GRID */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px', fontSize: '0.72rem', background: '#0f172a', padding: '8px 10px', borderRadius: '8px', border: '1px solid #334155' }}>
                          <div>
                            <span style={{ color: '#94a3b8', display: 'block', fontSize: '0.65rem' }}>Départ :</span>
                            <b style={{ color: '#cbd5e1' }}>{job.estimated_departure || '07:30'}</b>
                          </div>
                          <div>
                            <span style={{ color: '#94a3b8', display: 'block', fontSize: '0.65rem' }}>Retour :</span>
                            <b style={{ color: '#cbd5e1' }}>{calculatedReturnTime}</b>
                          </div>
                          <div>
                            <span style={{ color: '#94a3b8', display: 'block', fontSize: '0.65rem' }}>Distance :</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                              <Navigation size={11} color="#22c55e" />
                              <b style={{ color: '#22c55e' }}>{displayKm.toFixed(1)} km</b>
                            </div>
                          </div>
                          <div>
                            <span style={{ color: '#94a3b8', display: 'block', fontSize: '0.65rem' }}>Durée :</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                              <Clock size={11} color="#38bdf8" />
                              <b style={{ color: '#38bdf8' }}>{formatDurationSec(displaySec)}</b>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

          </div>
        )}

        {/* MISSION DETAIL VIEW */}
        {selectedJobId && currentJob && (() => {
          const truck = trucks.find(t => t.id === currentJob.truck_id);
          const startHub = hubs.find(h => h.id === currentJob.start_hub_id) || hubs[0] || { name: 'Dépôt Principal' };
          const endHub = hubs.find(h => h.id === currentJob.end_hub_id) || startHub;
          const displaySec = parseInt(currentJob.estimated_duration_sec || 0);
          const calculatedReturnTime = calculateReturnTime(currentJob.estimated_departure || '07:30', displaySec);

          return (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              
              {/* TOP MISSION HEADER */}
              <div style={{ background: '#1e293b', borderBottom: '1px solid #334155', padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <button
                  onClick={() => setSelectedJobId(null)}
                  style={{ background: 'none', border: 'none', color: '#38bdf8', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', fontWeight: '700', padding: 0 }}
                >
                  <ArrowLeft size={16} />
                  <span>Mes Tournées</span>
                </button>

                {/* TRUCK RETURNS & MAP / LIST VIEW TOGGLE BUTTONS */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <button
                    type="button"
                    onClick={() => setShowTruckContentModal(true)}
                    title="Vérifier les retours et contenu camion"
                    style={{
                      background: 'rgba(56, 189, 248, 0.12)',
                      color: '#38bdf8',
                      border: '1px solid rgba(56, 189, 248, 0.35)',
                      padding: '5px 9px',
                      borderRadius: '6px',
                      fontWeight: '800',
                      fontSize: '0.72rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    <Package size={13} color="#38bdf8" />
                    <span>Retours ({truckReturnedTotals.totalItemsCount})</span>
                  </button>
                  <button
                    onClick={() => setViewMode(viewMode === 'list' ? 'map' : 'list')}
                    style={{
                      background: viewMode === 'map' ? '#38bdf8' : '#0f172a',
                      color: viewMode === 'map' ? '#0f172a' : '#cbd5e1',
                      border: '1px solid #334155',
                      padding: '5px 10px',
                      borderRadius: '6px',
                      fontWeight: '800',
                      fontSize: '0.72rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    {viewMode === 'list' ? <Map size={13} /> : <List size={13} />}
                    <span>{viewMode === 'list' ? 'Vue Carte' : 'Vue Liste'}</span>
                  </button>

                  {/* PAUSE BUTTON */}
                  {currentJob.status === 'in_progress' && (
                    <button
                      onClick={handleTogglePause}
                      style={{
                        background: isPaused ? '#ef4444' : '#f59e0b',
                        color: '#0f172a',
                        border: 'none',
                        padding: '5px 10px',
                        borderRadius: '6px',
                        fontWeight: '900',
                        fontSize: '0.72rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      {isPaused ? <PlayCircle size={13} /> : <Pause size={13} fill="#0f172a" />}
                      <span>{isPaused ? 'Terminer Pause' : 'Mettre en Pause'}</span>
                    </button>
                  )}
                </div>
              </div>

              {/* MISSION ACTION BAR (START / FINISH) */}
              <div style={{ padding: '8px 14px', background: '#0f172a', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.74rem' }}>
                  <span style={{ color: '#94a3b8' }}>Statut :</span>
                  <span style={{ fontWeight: '800', color: currentJob.status === 'in_progress' ? '#38bdf8' : currentJob.status === 'completed' ? '#22c55e' : '#f59e0b' }}>
                    {currentJob.status === 'in_progress' ? 'En Cours' : currentJob.status === 'completed' ? 'Clôturée' : 'À démarrer'}
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {currentJob.status === 'planned' && (
                    <button
                      disabled={loading}
                      onClick={handleStartMission}
                      style={{ background: '#38bdf8', color: '#0f172a', border: 'none', padding: '6px 14px', borderRadius: '6px', fontWeight: '900', fontSize: '0.74rem', display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}
                    >
                      <Play size={13} fill="#0f172a" />
                      <span>Démarrer</span>
                    </button>
                  )}

                  {currentJob.status === 'in_progress' && allJobStopsValidated && (
                    <button
                      disabled={loading}
                      onClick={handleFinishMission}
                      style={{ background: '#22c55e', color: '#ffffff', border: 'none', padding: '6px 14px', borderRadius: '6px', fontWeight: '900', fontSize: '0.74rem', display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}
                    >
                      <Flag size={13} />
                      <span>Clôturer</span>
                    </button>
                  )}
                </div>
              </div>

              {/* MAP VIEW TAB */}
              {viewMode === 'map' && (
                <div style={{ flex: 1, minHeight: '460px', position: 'relative', display: 'flex', flexDirection: 'column' }}>
                  <MapView
                    hubs={hubs.filter(h => h.id === currentJob.start_hub_id || h.id === currentJob.end_hub_id)}
                    stops={currentJobStops}
                    plannedPolyline={currentJob.planned_polyline}
                    routeColor={truck?.color || '#38bdf8'}
                    geohistory={geohistory}
                    selectedStopId={activeStopModal?.id}
                    onSelectStop={(stopId) => {
                      const st = currentJobStops.find(s => s.id === stopId);
                      if (st) handleOpenStopModal(st);
                    }}
                  />

                  {/* FLOATING TOP-RIGHT DEBUG BUTTON */}
                  <div style={{ position: 'absolute', top: '12px', right: '12px', zIndex: 500, display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={() => setShowGeohistoryDebugModal(true)}
                      style={{
                        background: '#0f172a',
                        border: '1px solid #f59e0b',
                        color: '#f59e0b',
                        padding: '6px 12px',
                        borderRadius: '8px',
                        fontWeight: '800',
                        fontSize: '0.74rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        boxShadow: '0 4px 14px rgba(0,0,0,0.6)'
                      }}
                      title="Inspecter les coordonnées GPS enregistrées en base"
                    >
                      <span>🛰️ Géohistoire</span>
                      <span style={{ background: '#f59e0b', color: '#0f172a', borderRadius: '10px', padding: '1px 6px', fontSize: '0.68rem', fontWeight: '900' }}>
                        {geohistory.length} pts
                      </span>
                    </button>
                  </div>

                </div>
              )}

              {/* LIST VIEW TAB */}
              {viewMode === 'list' && (
                <div style={{ flex: 1, padding: '10px 4px 40px 4px' }}>
                  <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: '0.76rem' }}>
                    <tbody>
                      {/* DEPARTURE HUB ROW */}
                      <tr style={{ background: '#0f172a', borderBottom: '1px solid #334155' }}>
                        <td colSpan={5} style={{ padding: '6px 8px', color: '#94a3b8', fontSize: '0.72rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <Building size={14} color="#94a3b8" />
                              <span>Départ : <b style={{ color: currentJob.real_start ? '#22c55e' : '#cbd5e1' }}>{currentJob.real_start ? formatLocalTimeHHMM(currentJob.real_start) : (currentJob.estimated_departure || '07:30').slice(0, 5)}</b> ({startHub.name})</span>
                            </div>
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#1e293b', border: '1px solid #334155', borderRadius: '4px', padding: '2px 6px' }}>
                              <span style={{ fontSize: '0.68rem', color: '#94a3b8' }}>Km début :</span>
                              <input
                                type="number"
                                step="0.1"
                                placeholder="0.0"
                                value={startKm}
                                onChange={e => {
                                  isStartKmDirtyRef.current = true;
                                  setStartKm(e.target.value);
                                }}
                                onBlur={() => {
                                  isStartKmDirtyRef.current = false;
                                  handleUpdateJobKm('real_start_km', startKm);
                                }}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    e.target.blur();
                                    isStartKmDirtyRef.current = false;
                                    handleUpdateJobKm('real_start_km', startKm);
                                  }
                                }}
                                style={{ width: '55px', background: 'transparent', border: 'none', color: '#38bdf8', fontSize: '0.72rem', fontWeight: '700', outline: 'none' }}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  isStartKmDirtyRef.current = false;
                                  handleUpdateJobKm('real_start_km', startKm);
                                }}
                                title="Enregistrer Km début"
                                style={{
                                  background: savedStartKmIndicator ? '#15803d' : '#334155',
                                  color: '#ffffff',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '2px 5px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <Check size={11} color={savedStartKmIndicator ? '#86efac' : '#94a3b8'} />
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>

                      {/* TRAJET START HUB -> FIRST STOP & PAUSES */}
                      {currentJobStops.length > 0 && (() => {
                        const firstStop = currentJobStops[0];
                        const isJobClosed = currentJob.status === 'completed' || currentJob.status === 'done';
                        const isFirstStopDone = firstStop.status === 'delivered' || firstStop.status === 'failed';
                        const pausesBeforeFirst = (jobPauses || []).filter(p => {
                          if (parseInt(p.job_id) !== parseInt(currentJob.id)) return false;
                          if (parseInt(p.before_stop_id) !== parseInt(firstStop.id)) return false;
                          const isReal = p.type === 'real' || (p.pause_label && p.pause_label.toLowerCase().includes('réelle'));
                          if (isReal) return true;
                          return !isJobClosed && !isFirstStopDone;
                        });
                        const firstPauseMins = pausesBeforeFirst.reduce((acc, p) => acc + parseInt(p.duration_mins || p.estimated_length_mins || p.real_length_mins || 10), 0);
                        return (
                          <>
                            <tr style={{ background: '#0f172a' }}>
                              <td colSpan={5} style={{ padding: '5px 10px', borderBottom: '1px dashed #334155', color: '#94a3b8', fontSize: '0.7rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <Truck size={13} color="#94a3b8" />
                                  <span>Trajet : <b style={{ color: '#94a3b8', fontWeight: '400' }}>{calcHubToStopDriveMins(startHub, firstStop, currentJob.estimated_departure, firstPauseMins)} min&nbsp;&nbsp;&nbsp;{calcHaversineDistanceKm(startHub.latitude, startHub.longitude, firstStop.latitude, firstStop.longitude)} km</b></span>
                                </div>
                              </td>
                            </tr>
                            {pausesBeforeFirst.map(p => {
                              const isReal = p.type === 'real' || (p.pause_label && p.pause_label.toLowerCase().includes('réelle'));
                              const pauseDuration = parseInt(p.duration_mins || p.estimated_length_mins || p.real_length_mins || 10);
                              const timeWindowStr = p.start && p.end ? `(${p.start} - ${p.end})` : '';
                              return (
                                <tr key={`pause-first-${p.id}`} style={{ background: '#0f172a' }}>
                                  <td colSpan={5} style={{ padding: '4px 10px', borderBottom: '1px dashed #334155', textAlign: 'left' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.7rem', color: '#94a3b8' }}>
                                      <Pause size={13} color="#94a3b8" />
                                      <span>
                                        {isReal ? (
                                          <>
                                            <span>Pause réelle : </span>
                                            <b style={{ color: '#22c55e', fontWeight: '700' }}>{pauseDuration} min</b>
                                            {timeWindowStr ? <span> {timeWindowStr}</span> : ''}
                                          </>
                                        ) : (
                                          <>
                                            <span>Pause : </span>
                                            <b style={{ color: '#94a3b8', fontWeight: '500' }}>{pauseDuration} min</b>
                                            {timeWindowStr ? <span> {timeWindowStr}</span> : ''}
                                          </>
                                        )}
                                      </span>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </>
                        );
                      })()}

                      {/* ALL STOPS */}
                      {currentJobStops.map((stop, idx) => {
                        const client = clients.find(c => c.id === stop.client_id);
                        const isDoneStop = stop.status === 'delivered' || stop.status === 'failed';
                        const isFailedStop = stop.status === 'failed';
                        const stopDisplayNum = stop.real_index || stop.estimated_index || (idx + 1);

                        const arrivalTimeFormatted = stop.estimated_arrival ? stop.estimated_arrival.slice(0, 5) : '08:00';
                        const serviceMins = parseInt(stop.service_time_mins || client?.default_service_time_mins || 10, 10) || 10;
                        const departureTimeFormatted = calcDepartureTimeStr(arrivalTimeFormatted, serviceMins);

                        // Stop timings 4-state logic (Real = Green, Estimated/Unexecuted = Grey)
                        const hasRealArrival = !!stop.actual_arrival;

                        let arrivalDisplay = arrivalTimeFormatted;
                        let arrivalColor = '#94a3b8';
                        let arrivalWeight = '500';
                        let arrivalTitle = "Heure d'arrivée estimée";

                        let serviceDisplay = `${serviceMins}m`;
                        let serviceColor = '#94a3b8';
                        let serviceWeight = '500';
                        let serviceTitle = "Temps de service estimé";

                        let departureDisplay = departureTimeFormatted;
                        let departureColor = '#64748b';
                        let departureWeight = '500';
                        let departureTitle = "Heure de départ estimée";

                        if (hasRealArrival && !isDoneStop) {
                          // State 2: marked as arrived and nothing else -> Green real arrival, Grey estimated service & departure based on arrival
                          arrivalDisplay = formatLocalTimeHHMM(stop.actual_arrival);
                          arrivalColor = '#22c55e';
                          arrivalWeight = '700';
                          arrivalTitle = "Heure d'arrivée réelle";

                          serviceColor = '#94a3b8';
                          serviceWeight = '500';
                          serviceTitle = "Temps de service estimé";

                          departureDisplay = calcDepartureTimeStr(arrivalDisplay, serviceMins);
                          departureColor = '#64748b';
                          departureWeight = '500';
                          departureTitle = "Heure de départ estimée";
                        } else if (!hasRealArrival && isDoneStop) {
                          // State 3: validated and never marked as arrived -> Grey estimated arrival & service, Green real departure
                          arrivalDisplay = arrivalTimeFormatted;
                          arrivalColor = '#94a3b8';
                          arrivalWeight = '500';
                          arrivalTitle = "Heure d'arrivée estimée";

                          serviceColor = '#94a3b8';
                          serviceWeight = '500';
                          serviceTitle = "Temps de service estimé";

                          departureDisplay = stop.real_departure ? formatLocalTimeHHMM(stop.real_departure) : departureTimeFormatted;
                          departureColor = '#22c55e';
                          departureWeight = '700';
                          departureTitle = "Heure de départ réelle";
                        } else if (hasRealArrival && isDoneStop) {
                          // State 4: both marked as arrived and validated -> Green real arrival, Green real computed service, Green real departure
                          arrivalDisplay = formatLocalTimeHHMM(stop.actual_arrival);
                          arrivalColor = '#22c55e';
                          arrivalWeight = '700';
                          arrivalTitle = "Heure d'arrivée réelle";

                          let realServiceMins = serviceMins;
                          if (stop.real_departure && stop.actual_arrival) {
                            const arrDate = parseToDate(stop.actual_arrival);
                            const depDate = parseToDate(stop.real_departure);
                            if (arrDate && depDate) {
                              const diffMs = depDate.getTime() - arrDate.getTime();
                              if (!isNaN(diffMs) && diffMs >= 0) {
                                realServiceMins = Math.max(1, Math.round(diffMs / 60000));
                              }
                            }
                          }
                          serviceDisplay = `${realServiceMins}m`;
                          serviceColor = '#22c55e';
                          serviceWeight = '700';
                          serviceTitle = "Temps de service réel";

                          departureDisplay = stop.real_departure ? formatLocalTimeHHMM(stop.real_departure) : departureTimeFormatted;
                          departureColor = '#22c55e';
                          departureWeight = '700';
                          departureTitle = "Heure de départ réelle";
                        } else {
                          // State 1: not arrived, still planned -> All values are estimated and grey
                          arrivalDisplay = arrivalTimeFormatted;
                          arrivalColor = '#94a3b8';
                          arrivalWeight = '500';
                          arrivalTitle = "Heure d'arrivée estimée";

                          serviceDisplay = `${serviceMins}m`;
                          serviceColor = '#94a3b8';
                          serviceWeight = '500';
                          serviceTitle = "Temps de service estimé";

                          departureDisplay = departureTimeFormatted;
                          departureColor = '#64748b';
                          departureWeight = '500';
                          departureTitle = "Heure de départ estimée";
                        }

                        return (
                          <React.Fragment key={stop.id}>
                            <tr
                              onClick={() => handleOpenStopModal(stop)}
                              style={{
                                background: '#1e293b',
                                borderBottom: '1px solid #334155',
                                cursor: 'pointer'
                              }}
                            >
                              {/* COL 0: STOP NUMBER BADGE (VERY LEFT) */}
                              <td style={{ padding: '6px 2px', textAlign: 'center', width: '26px', verticalAlign: 'middle' }}>
                                <span
                                  style={{
                                    background: isFailedStop ? '#ef4444' : isDoneStop ? '#22c55e' : '#38bdf8',
                                    color: isFailedStop ? '#ffffff' : '#0f172a',
                                    width: '18px',
                                    height: '18px',
                                    borderRadius: '50%',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: '800',
                                    fontSize: '0.7rem',
                                    flexShrink: 0
                                  }}
                                >
                                  {stopDisplayNum}
                                </span>
                              </td>

                              {/* COL 1: TIMINGS STACK (4 STATES DYNAMIC TIMINGS) */}
                              <td style={{ padding: '6px 2px', textAlign: 'center', width: '46px', verticalAlign: 'middle' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'center' }}>
                                  <span style={{ fontSize: '0.72rem', fontWeight: arrivalWeight, color: arrivalColor }} title={arrivalTitle}>
                                    {arrivalDisplay}
                                  </span>
                                  <span style={{ fontSize: '0.66rem', fontWeight: serviceWeight, color: serviceColor }} title={serviceTitle}>
                                    {serviceDisplay}
                                  </span>
                                  <span style={{ fontSize: '0.7rem', fontWeight: departureWeight, color: departureColor }} title={departureTitle}>
                                    {departureDisplay}
                                  </span>
                                </div>
                              </td>

                              {/* COL 2: REAL TIME WINDOWS */}
                              <td style={{ padding: '6px 2px', textAlign: 'center', verticalAlign: 'middle', width: '74px' }}>
                                {renderStopRealTimeWindows(stop, client, arrivalTimeFormatted)}
                              </td>

                              {/* COL 3: CLIENT & ADDRESS (WRAPS NATURALLY) */}
                              <td style={{ padding: '6px 4px', verticalAlign: 'middle', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                                  <strong style={{ color: '#ffffff', fontSize: '0.78rem', wordBreak: 'break-word', whiteSpace: 'normal', overflowWrap: 'break-word', lineHeight: '1.25' }}>
                                    {stop.client_name}
                                  </strong>
                                  <div style={{ fontSize: '0.68rem', color: '#94a3b8', wordBreak: 'break-word', whiteSpace: 'normal', overflowWrap: 'break-word', lineHeight: '1.2' }}>
                                    {stop.address}, {stop.city}
                                  </div>
                                </div>
                              </td>

                              {/* COL 4: WEIGHT & VOLUME */}
                              <td style={{ padding: '6px 4px 6px 2px', textAlign: 'right', verticalAlign: 'middle', width: '52px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'flex-end', minWidth: '46px' }}>
                                  <span style={{ fontSize: '0.72rem', fontWeight: stop.type === 'pickup' ? '600' : '400', color: stop.type === 'pickup' ? '#22c55e' : '#94a3b8' }}>
                                    {stop.type === 'pickup' ? '+' : '-'}{Math.round(stop.weight_kg || 0)} kg
                                  </span>
                                  <span style={{ fontSize: '0.72rem', fontWeight: stop.type === 'pickup' ? '600' : '400', color: stop.type === 'pickup' ? '#22c55e' : '#94a3b8' }}>
                                    {stop.type === 'pickup' ? '+' : '-'}{stop.volume_m3 || 0.25} m³
                                  </span>
                                </div>
                              </td>
                            </tr>

                            {/* INTER-STOP TRAJET & PAUSES */}
                            {idx < currentJobStops.length - 1 && (() => {
                              const nextStop = currentJobStops[idx + 1];
                              const isJobClosed = currentJob.status === 'completed' || currentJob.status === 'done';
                              const isNextStopDone = nextStop.status === 'delivered' || nextStop.status === 'failed';
                              const pausesForNextStop = (jobPauses || []).filter(p => {
                                if (parseInt(p.job_id) !== parseInt(currentJob.id)) return false;
                                if (parseInt(p.before_stop_id) !== parseInt(nextStop.id)) return false;
                                const isReal = p.type === 'real' || (p.pause_label && p.pause_label.toLowerCase().includes('réelle'));
                                if (isReal) return true;
                                return !isJobClosed && !isNextStopDone;
                              });

                              const prevDepTime = stop.real_departure || stop.actual_arrival;
                              const nextArrTime = nextStop.actual_arrival;

                              let isRealTrajet = false;
                              let trajetMins = calcLegDriveTimeMins(stop, nextStop);

                              if (prevDepTime && nextArrTime) {
                                const tDep = new Date(prevDepTime).getTime();
                                const tArr = new Date(nextArrTime).getTime();
                                if (!isNaN(tDep) && !isNaN(tArr) && tArr >= tDep) {
                                  isRealTrajet = true;
                                  trajetMins = Math.max(1, Math.round((tArr - tDep) / 60000));
                                }
                              }

                              return (
                                <React.Fragment key={`leg-${stop.id}`}>
                                  <tr style={{ background: '#0f172a' }}>
                                    <td colSpan={5} style={{ padding: '5px 12px', borderTop: '1px dashed #334155', borderBottom: '1px dashed #334155', textAlign: 'left' }}>
                                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.7rem', color: '#94a3b8' }}>
                                        <Truck size={13} color="#94a3b8" />
                                        <span>Trajet : <b style={{ color: isRealTrajet ? '#22c55e' : '#94a3b8', fontWeight: isRealTrajet ? '700' : '400' }}>{trajetMins} min</b>&nbsp;&nbsp;&nbsp;<span style={{ color: '#94a3b8' }}>{calcHaversineDistanceKm(stop.latitude, stop.longitude, nextStop.latitude, nextStop.longitude)} km</span></span>
                                      </div>
                                    </td>
                                  </tr>

                                  {pausesForNextStop.map(p => {
                                    const isReal = p.type === 'real' || (p.pause_label && p.pause_label.toLowerCase().includes('réelle'));
                                    const pauseDuration = parseInt(p.duration_mins || p.estimated_length_mins || p.real_length_mins || 10);
                                    const timeWindowStr = p.start && p.end ? `(${p.start} - ${p.end})` : '';
                                    return (
                                      <tr key={`pause-${p.id}`} style={{ background: '#0f172a' }}>
                                        <td colSpan={5} style={{ padding: '4px 12px', borderBottom: '1px dashed #334155', textAlign: 'left' }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.7rem', color: '#94a3b8' }}>
                                            <Pause size={13} color="#94a3b8" />
                                            <span>
                                              {isReal ? (
                                                <>
                                                  <span>Pause réelle : </span>
                                                  <b style={{ color: '#22c55e', fontWeight: '700' }}>{pauseDuration} min</b>
                                                  {timeWindowStr ? <span> {timeWindowStr}</span> : ''}
                                                </>
                                              ) : (
                                                <>
                                                  <span>Pause : </span>
                                                  <b style={{ color: '#94a3b8', fontWeight: '500' }}>{pauseDuration} min</b>
                                                  {timeWindowStr ? <span> {timeWindowStr}</span> : ''}
                                                </>
                                              )}
                                            </span>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </React.Fragment>
                              );
                            })()}
                          </React.Fragment>
                        );
                      })}

                      {/* TRAILING PAUSES */}
                      {(jobPauses || []).filter(p => {
                        if (parseInt(p.job_id) !== parseInt(currentJob.id)) return false;
                        if (currentJobStops.some(s => parseInt(s.id) === parseInt(p.before_stop_id))) return false;
                        const isReal = p.type === 'real' || (p.pause_label && p.pause_label.toLowerCase().includes('réelle'));
                        if (isReal) return true;
                        const isJobClosed = currentJob.status === 'completed' || currentJob.status === 'done';
                        const hasPendingStops = currentJobStops.some(s => s.status === 'pending');
                        return !isJobClosed && hasPendingStops;
                      }).map(p => {
                        const isReal = p.type === 'real' || (p.pause_label && p.pause_label.toLowerCase().includes('réelle'));
                        const pauseDuration = parseInt(p.duration_mins || p.estimated_length_mins || p.real_length_mins || 10);
                        const timeWindowStr = p.start && p.end ? `(${p.start} - ${p.end})` : '';
                        return (
                          <tr key={`pause-trailing-${p.id}`} style={{ background: '#0f172a' }}>
                            <td colSpan={5} style={{ padding: '4px 12px', borderBottom: '1px dashed #334155', textAlign: 'left' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.7rem', color: '#94a3b8' }}>
                                <Pause size={13} color="#94a3b8" />
                                <span>
                                  {isReal ? (
                                    <>
                                      <span>Pause réelle : </span>
                                      <b style={{ color: '#22c55e', fontWeight: '700' }}>{pauseDuration} min</b>
                                      {timeWindowStr ? <span> {timeWindowStr}</span> : ''}
                                    </>
                                  ) : (
                                    <>
                                      <span>Pause : </span>
                                      <b style={{ color: '#94a3b8', fontWeight: '500' }}>{pauseDuration} min</b>
                                      {timeWindowStr ? <span> {timeWindowStr}</span> : ''}
                                    </>
                                  )}
                                </span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}

                      {/* TRAJET END HUB */}
                      {currentJobStops.length > 0 && (
                        <tr style={{ background: '#0f172a' }}>
                          <td colSpan={5} style={{ padding: '5px 12px', borderTop: '1px dashed #334155', fontSize: '0.7rem', color: '#94a3b8' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <Truck size={13} color="#94a3b8" />
                              <span>Trajet retour : <b style={{ color: '#94a3b8', fontWeight: '400' }}>{calcStopToHubDriveMins(currentJobStops[currentJobStops.length - 1], endHub, calculatedReturnTime)} min&nbsp;&nbsp;&nbsp;{calcHaversineDistanceKm(currentJobStops[currentJobStops.length - 1].latitude, currentJobStops[currentJobStops.length - 1].longitude, endHub.latitude, endHub.longitude)} km</b></span>
                            </div>
                          </td>
                        </tr>
                      )}

                      {/* RETURN HUB ROW */}
                      <tr style={{ background: '#0f172a', borderTop: '1px solid #334155' }}>
                        <td colSpan={5} style={{ padding: '6px 10px', color: '#94a3b8', fontSize: '0.72rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <Building size={14} color="#94a3b8" />
                              <span>Retour : <b style={{ color: currentJob.real_end ? '#22c55e' : '#cbd5e1' }}>{currentJob.real_end ? formatLocalTimeHHMM(currentJob.real_end) : calculatedReturnTime}</b> ({endHub.name})</span>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#1e293b', border: '1px solid #334155', borderRadius: '4px', padding: '2px 6px' }}>
                                <span style={{ fontSize: '0.68rem', color: '#94a3b8' }}>Km fin :</span>
                                <input
                                  type="number"
                                  step="0.1"
                                  placeholder="0.0"
                                  value={endKm}
                                  onChange={e => {
                                    isEndKmDirtyRef.current = true;
                                    setEndKm(e.target.value);
                                  }}
                                  onBlur={() => {
                                    isEndKmDirtyRef.current = false;
                                    handleUpdateJobKm('real_end_km', endKm);
                                  }}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                      e.target.blur();
                                      isEndKmDirtyRef.current = false;
                                      handleUpdateJobKm('real_end_km', endKm);
                                    }
                                  }}
                                  style={{ width: '55px', background: 'transparent', border: 'none', color: '#22c55e', fontSize: '0.72rem', fontWeight: '700', outline: 'none' }}
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    isEndKmDirtyRef.current = false;
                                    handleUpdateJobKm('real_end_km', endKm);
                                  }}
                                  title="Enregistrer Km fin"
                                  style={{
                                    background: savedEndKmIndicator ? '#15803d' : '#334155',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '2px 5px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <Check size={11} color={savedEndKmIndicator ? '#86efac' : '#94a3b8'} />
                                </button>
                              </div>

                              {currentJob.status !== 'completed' && allJobStopsValidated && (
                                <button
                                  disabled={loading}
                                  onClick={handleFinishMission}
                                  style={{ background: '#22c55e', color: '#ffffff', border: 'none', padding: '4px 10px', borderRadius: '6px', fontWeight: '800', fontSize: '0.72rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                                >
                                  <Flag size={12} />
                                  <span>Clôturer la Tournée</span>
                                </button>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  {/* DEDICATED BUTTON TO VERIFY EMPTY RETURNS / TRUCK CONTENT */}
                  <div style={{ marginTop: '16px', padding: '0 4px' }}>
                    <button
                      type="button"
                      onClick={() => setShowTruckContentModal(true)}
                      style={{
                        width: '100%',
                        padding: '13px 16px',
                        background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                        border: '1px solid #38bdf8',
                        borderRadius: '12px',
                        color: '#ffffff',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        boxShadow: '0 4px 14px rgba(56, 189, 248, 0.15)',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ width: '38px', height: '38px', borderRadius: '8px', background: 'rgba(56, 189, 248, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Package size={22} color="#38bdf8" />
                        </div>
                        <div style={{ textAlign: 'left' }}>
                          <div style={{ fontWeight: '800', fontSize: '0.86rem', color: '#f8fafc' }}>
                            Vérification Retours & Camion
                          </div>
                          <div style={{ fontSize: '0.70rem', color: '#94a3b8' }}>
                            {truckReturnedTotals.totalItemsCount} objet{truckReturnedTotals.totalItemsCount > 1 ? 's' : ''} récupéré{truckReturnedTotals.totalItemsCount > 1 ? 's' : ''} • {jobReturnTickets.length} bon{jobReturnTickets.length > 1 ? 's' : ''} validé{jobReturnTickets.length > 1 ? 's' : ''}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '0.84rem', fontWeight: '800', color: '#22c55e' }}>
                          {truckReturnedTotals.totalValue.toFixed(2)} CHF
                        </span>
                        <ChevronRight size={16} color="#94a3b8" />
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {/* ---------------------------------------------------- */}
              {/* UPGRADED STOP DETAIL & VALIDATION POPUP */}
              {/* ---------------------------------------------------- */}
              {activeStopModal && (() => {
                const client = clients.find(c => c.id === activeStopModal.client_id);
                const matchingContacts = clientContacts.filter(c => c.client_id === activeStopModal.client_id);
                const isValidated = activeStopModal.status === 'delivered' || activeStopModal.status === 'failed';
                const hasArrival = !!activeStopModal.actual_arrival;
                const isMissionStarted = currentJob && currentJob.status === 'in_progress';
                const stopIdx = currentJobStops.findIndex(s => s.id === activeStopModal.id);
                const stopDisplayNum = activeStopModal.real_index || activeStopModal.estimated_index || (stopIdx >= 0 ? stopIdx + 1 : activeStopModal.id);
                const clientPhone = matchingContacts.find(c => c.phone)?.phone || client?.phone || activeStopModal.phone;
                const hasClientInfo = Boolean((client?.roadbook && client.roadbook.trim()) || matchingContacts.length > 0);
                const hasPostit = Boolean(clientPostitText && clientPostitText.trim().length > 0);

                const driverSigAtt = stopAttachments.find(a => a.attachment_type === 'driver_signature');
                const recipientSigAtt = stopAttachments.find(a => a.attachment_type === 'recipient_signature');
                const recipientNameAtt = stopAttachments.find(a => a.attachment_type === 'recipient_signature_name');
                const docAttachments = stopAttachments.filter(a => a.attachment_type !== 'driver_signature' && a.attachment_type !== 'recipient_signature' && a.attachment_type !== 'recipient_signature_name');

                const totalReturnTicketValue = stopReturnTickets.reduce((acc, ticket) => {
                  return acc + parseFloat(ticket.validated_total_value || 0);
                }, 0);

                return (
                  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '12px' }}>
                    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '14px', width: '100%', maxWidth: '460px', maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.5)' }}>
                      
                      {/* MODAL HEADER: STOP INDEX CIRCLE & STOP ID */}
                      <div style={{ background: '#0f172a', padding: '12px 16px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '50%',
                            background: activeStopModal.status === 'delivered'
                              ? '#22c55e'
                              : (activeStopModal.status === 'failed' || selectedProblemReasons.length > 0)
                                ? '#ef4444'
                                : '#0284c7',
                            color: '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: '900',
                            fontSize: '0.84rem',
                            flexShrink: 0,
                            boxShadow: activeStopModal.status === 'delivered'
                              ? '0 0 10px rgba(34, 197, 94, 0.4)'
                              : (activeStopModal.status === 'failed' || selectedProblemReasons.length > 0)
                                ? '0 0 10px rgba(239, 68, 68, 0.4)'
                                : '0 0 10px rgba(2, 132, 199, 0.4)'
                          }}>
                            {stopDisplayNum}
                          </div>
                          <span style={{ fontSize: '0.88rem', fontWeight: '800', color: '#ffffff' }}>
                            {activeStopModal.stop_number || activeStopModal.client_code || `Commande #${activeStopModal.id}`}
                          </span>
                        </div>

                        <button onClick={() => setActiveStopModal(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px' }}>
                          <X size={20} />
                        </button>
                      </div>

                      {/* MODAL BODY (SCROLLABLE) */}
                      <div style={{ flex: 1, minHeight: 0, padding: '14px 16px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        
                        {/* NOT STARTED WARNING */}
                        {!isMissionStarted && (
                          <div style={{ background: 'rgba(245, 158, 11, 0.12)', border: '1px solid #f59e0b', borderRadius: '8px', padding: '8px 12px', color: '#f59e0b', fontSize: '0.74rem', display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                            <AlertTriangle size={14} color="#f59e0b" />
                            <span>Tournée non démarrée : Démarrez la tournée pour marquer l'arrivée ou valider.</span>
                          </div>
                        )}

                        {/* ENCADRÉ 1: CLIENT DETAILS & ACTIONS */}
                        <div style={{ background: '#0f172a', padding: '12px 14px', borderRadius: '10px', border: '1px solid #334155', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                            <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#ffffff', fontWeight: '800', flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
                              {activeStopModal.client_name}
                            </h3>
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0 }}>
                              {/* 1. GPS Button */}
                              <button
                                type="button"
                                title="Ouvrir dans Google Maps"
                                onClick={() => {
                                  if (activeStopModal.latitude && activeStopModal.longitude) {
                                    window.open(`https://www.google.com/maps/dir/?api=1&destination=${activeStopModal.latitude},${activeStopModal.longitude}`, '_blank');
                                  } else {
                                    const q = encodeURIComponent(`${activeStopModal.address}, ${activeStopModal.city}`);
                                    window.open(`https://www.google.com/maps/dir/?api=1&destination=${q}`, '_blank');
                                  }
                                }}
                                style={{
                                  background: '#0284c7',
                                  color: '#ffffff',
                                  border: 'none',
                                  width: '32px',
                                  height: '32px',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <Navigation size={15} />
                              </button>

                              {/* 2. Phone Call Button */}
                              {clientPhone ? (
                                <a
                                  href={`tel:${clientPhone}`}
                                  title={`Appeler ${clientPhone}`}
                                  style={{
                                    background: '#0284c7',
                                    color: '#ffffff',
                                    textDecoration: 'none',
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '8px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <Phone size={15} />
                                </a>
                              ) : (
                                <button
                                  disabled
                                  title="Aucun numéro de téléphone"
                                  style={{
                                    background: '#1e293b',
                                    color: '#475569',
                                    border: '1px solid #334155',
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '8px',
                                    cursor: 'not-allowed',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <Phone size={15} />
                                </button>
                              )}

                              {/* 3. Info Button ("i") */}
                              {hasClientInfo ? (
                                <button
                                  type="button"
                                  title="Informations client & contacts"
                                  onClick={() => setShowClientInfoModal(true)}
                                  style={{
                                    background: '#0284c7',
                                    color: '#ffffff',
                                    border: 'none',
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <Info size={15} />
                                </button>
                              ) : (
                                <button
                                  disabled
                                  title="Aucune information client disponible"
                                  style={{
                                    background: '#1e293b',
                                    color: '#475569',
                                    border: '1px solid #334155',
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '8px',
                                    cursor: 'not-allowed',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <Info size={15} />
                                </button>
                              )}

                              {/* 4. Post-it Button (Note) */}
                              <button
                                type="button"
                                title={hasPostit ? "Consigne chauffeur enregistrée (cliquer pour voir/modifier)" : "Ajouter un post-it chauffeur"}
                                onClick={() => setShowPostitModal(true)}
                                style={{
                                  background: hasPostit ? '#ca8a04' : '#1e293b',
                                  color: hasPostit ? '#ffffff' : '#94a3b8',
                                  border: hasPostit ? 'none' : '1px solid #334155',
                                  width: '32px',
                                  height: '32px',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  boxShadow: hasPostit ? '0 0 10px rgba(202, 138, 4, 0.4)' : 'none'
                                }}
                              >
                                {hasPostit ? <FileText size={15} /> : <File size={15} />}
                              </button>
                            </div>
                          </div>

                          <div style={{ fontSize: '0.74rem', color: '#94a3b8' }}>
                            {activeStopModal.address}, {activeStopModal.city}
                          </div>
                        </div>

                        {/* ENCADRÉ 2: STOP METRICS & INSTRUCTIONS */}
                        <div style={{ borderRadius: '10px', overflow: 'hidden', border: '1px solid #334155', flexShrink: 0 }}>
                          <div style={{ background: '#0f172a', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {activeStopModal.instructions && activeStopModal.instructions.trim() && (
                              <div style={{ fontSize: '0.75rem', color: '#cbd5e1' }}>
                                <b style={{ color: '#94a3b8' }}>Instructions de livraison :</b> {activeStopModal.instructions}
                              </div>
                            )}

                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', paddingTop: '4px', borderTop: '1px dashed #334155' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <span style={{ fontSize: '0.78rem', fontWeight: '700', color: '#ffffff' }}>
                                  ⚖️ {Math.round(activeStopModal.weight_kg || 0)} kg
                                </span>
                                <span style={{ fontSize: '0.78rem', fontWeight: '700', color: '#ffffff' }}>
                                  📦 {activeStopModal.volume_m3 || 0.25} m³
                                </span>
                              </div>
                              <div>
                                {renderStopRealTimeWindows(activeStopModal, client, activeStopModal.estimated_arrival?.slice(0, 5))}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* ENCADRÉ 3: OBJETS CONSIGNÉS / BONS DE RETOUR */}
                        <div style={{ background: '#0f172a', padding: '10px 12px', borderRadius: '10px', border: '1px solid #334155', display: 'flex', flexDirection: 'column', gap: '8px', flexShrink: 0 }}>
                          <div style={{ fontSize: '0.76rem', fontWeight: '800', color: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <Package size={14} />
                              <span>Objets consignés</span>
                            </div>
                            <button
                              type="button"
                              title={stopReturnTickets.length > 0 ? 'Ajouter un autre bon de retour' : 'Ajouter un bon de retour'}
                              onClick={() => {
                                setEditingTicketId(null);
                                setEditingTicketStopId(activeStopModal.id);
                                setTicketOrderId('');
                                setReturnTicketTab('returned');
                                setTicketLines(objects.map(o => ({ object_id: o.id, amount: 0, unit_price: parseFloat(o.price || 0) })));
                                setActiveActionModal('return_ticket');
                              }}
                              style={{
                                background: 'rgba(245, 158, 11, 0.15)',
                                border: '1px solid #f59e0b',
                                color: '#f59e0b',
                                padding: '4px 8px',
                                borderRadius: '6px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '3px',
                                cursor: 'pointer'
                              }}
                            >
                              <Plus size={12} />
                              <Package size={14} />
                            </button>
                          </div>

                          {stopReturnTickets.length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              {stopReturnTickets.map(ticket => (
                                <div key={ticket.id} style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '6px', padding: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  {ticket.lines && ticket.lines.map((l, lIdx) => {
                                    const objDef = objects.find(o => o.id === l.object_id);
                                    const objName = l.object_name || objDef?.name || 'Objet';
                                    return (
                                      <div key={lIdx} style={{ fontSize: '0.72rem', color: '#cbd5e1', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span>• {objName}</span>
                                        <span style={{ fontWeight: '700', color: l.amount > 0 ? '#22c55e' : '#cbd5e1' }}>
                                          {l.amount > 0 ? `+${l.amount}` : l.amount} × {parseFloat(l.unit_price || 0).toFixed(2)} CHF = {((l.amount || 0) * (l.unit_price || 0)).toFixed(2)} CHF
                                        </span>
                                      </div>
                                    );
                                  })}
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.72rem', paddingTop: '4px', borderTop: '1px dashed #334155', marginTop: '3px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                      <span style={{ color: '#94a3b8' }}>Bon #{ticket.id}</span>
                                      <button
                                        type="button"
                                        title="Modifier ce bon de retour"
                                        onClick={() => handleOpenEditReturnTicket(ticket)}
                                        style={{
                                          background: 'rgba(56, 189, 248, 0.15)',
                                          border: '1px solid #0284c7',
                                          color: '#38bdf8',
                                          padding: '2px 5px',
                                          borderRadius: '4px',
                                          fontSize: '0.66rem',
                                          fontWeight: '700',
                                          cursor: 'pointer',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '2px'
                                        }}
                                      >
                                        <Edit3 size={10} />
                                        <span>Modifier</span>
                                      </button>
                                      <button
                                        type="button"
                                        title="Supprimer ce bon de retour"
                                        onClick={() => handleDeleteReturnTicket(ticket.id)}
                                        style={{
                                          background: 'rgba(239, 68, 68, 0.15)',
                                          border: '1px solid #dc2626',
                                          color: '#f87171',
                                          padding: '2px 5px',
                                          borderRadius: '4px',
                                          fontSize: '0.66rem',
                                          fontWeight: '700',
                                          cursor: 'pointer',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '2px'
                                        }}
                                      >
                                        <Trash2 size={10} />
                                        <span>Supprimer</span>
                                      </button>
                                    </div>
                                    <span style={{ fontWeight: '800', color: '#22c55e' }}>
                                      {parseFloat(ticket.validated_total_value || 0).toFixed(2)} CHF
                                    </span>
                                  </div>
                                </div>
                              ))}
                              {stopReturnTickets.length > 1 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.74rem', fontWeight: '800', color: '#ffffff', padding: '2px 4px' }}>
                                  <span>Total retour :</span>
                                  <span style={{ color: '#22c55e' }}>{totalReturnTicketValue.toFixed(2)} CHF</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* ENCADRÉ 4: SIGNATURES (SIDE-BY-SIDE CARDS MATCHING REFERENCE) */}
                        <div style={{ background: '#0f172a', padding: '10px 12px', borderRadius: '10px', border: '1px solid #334155', display: 'flex', flexDirection: 'column', gap: '8px', flexShrink: 0 }}>
                          <div style={{ fontSize: '0.76rem', fontWeight: '800', color: '#22c55e', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <PenTool size={14} />
                            <span>Signatures</span>
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                            {/* LIVREUR SIGNATURE CARD */}
                            <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                              <div style={{ background: '#064e3b', borderBottom: '1px solid #047857', padding: '5px 8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <PenTool size={12} color="#22c55e" />
                                <span style={{ fontSize: '0.72rem', fontWeight: '800', color: '#ffffff' }}>Livreur</span>
                              </div>
                              <div style={{ padding: '8px', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '6px' }}>
                                <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                                  {driver?.first_name ? `${driver.first_name} ${driver.last_name}` : 'Livreur'}
                                </div>

                                {driverSigAtt ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                                    <img
                                      src={resolveAssetUrl(driverSigAtt.value)}
                                      alt="Signature Livreur"
                                      style={{ maxHeight: '55px', maxWidth: '100%', objectFit: 'contain', background: '#ffffff', borderRadius: '4px', padding: '2px' }}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteAttachment(driverSigAtt.id)}
                                      style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '0.65rem', cursor: 'pointer', padding: '2px 4px' }}
                                    >
                                      ✕ Effacer
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => setActiveActionModal('driver_sig')}
                                    style={{
                                      background: 'rgba(34, 197, 94, 0.15)',
                                      border: '1px solid #22c55e',
                                      color: '#22c55e',
                                      padding: '6px 8px',
                                      borderRadius: '6px',
                                      fontSize: '0.72rem',
                                      fontWeight: '700',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      gap: '4px'
                                    }}
                                  >
                                    <PenTool size={11} />
                                    <span>Signer</span>
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* CLIENT SIGNATURE CARD */}
                            <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                              <div style={{ background: '#064e3b', borderBottom: '1px solid #047857', padding: '5px 8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <PenTool size={12} color="#22c55e" />
                                <span style={{ fontSize: '0.72rem', fontWeight: '800', color: '#ffffff' }}>Client</span>
                              </div>
                              <div style={{ padding: '8px', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '6px' }}>
                                <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                                  {recipientNameAtt?.value || 'Destinataire'}
                                </div>

                                {recipientSigAtt ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                                    <img
                                      src={resolveAssetUrl(recipientSigAtt.value)}
                                      alt="Signature Client"
                                      style={{ maxHeight: '55px', maxWidth: '100%', objectFit: 'contain', background: '#ffffff', borderRadius: '4px', padding: '2px' }}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteRecipientSignature(recipientSigAtt.id, recipientNameAtt?.id)}
                                      style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '0.65rem', cursor: 'pointer', padding: '2px 4px' }}
                                    >
                                      ✕ Effacer
                                    </button>
                                  </div>
                                ) : (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const clientContactsMatching = clientContacts.filter(c => c.client_id === activeStopModal.client_id);
                                        setRecipientContactId(clientContactsMatching[0]?.id ? String(clientContactsMatching[0].id) : 'other');
                                        setRecipientCustomLastName('');
                                        setRecipientCustomFirstName('');
                                        setActiveActionModal('recipient_sig');
                                      }}
                                      style={{
                                        background: 'rgba(34, 197, 94, 0.15)',
                                        border: '1px solid #22c55e',
                                        color: '#22c55e',
                                        padding: '6px 8px',
                                        borderRadius: '6px',
                                        fontSize: '0.72rem',
                                        fontWeight: '700',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '4px'
                                      }}
                                    >
                                      <PenTool size={11} />
                                      <span>Signer</span>
                                    </button>

                                    {previousClientSigUrl && (
                                      <button
                                        type="button"
                                        onClick={handleCopyClientSignature}
                                        style={{
                                          background: 'rgba(56, 189, 248, 0.15)',
                                          border: '1px solid #38bdf8',
                                          color: '#38bdf8',
                                          padding: '5px 8px',
                                          borderRadius: '6px',
                                          fontSize: '0.68rem',
                                          fontWeight: '700',
                                          cursor: 'pointer',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          gap: '4px'
                                        }}
                                        title={`Copier la signature client (${previousClientSignerName}) du stop #${otherStopSameClient?.real_index || otherStopSameClient?.sequence_order || otherStopSameClient?.id}`}
                                      >
                                        <Copy size={11} />
                                        <span>Copier sig. {previousClientSignerName ? `(${previousClientSignerName})` : ''}</span>
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* ENCADRÉ 5: PIÈCES JOINTES (PHOTOS / DOCS) */}
                        <div style={{ background: '#0f172a', padding: '10px 12px', borderRadius: '10px', border: '1px solid #334155', display: 'flex', flexDirection: 'column', gap: '8px', flexShrink: 0 }}>
                          <div style={{ fontSize: '0.76rem', fontWeight: '800', color: '#38bdf8', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <ImageIcon size={14} />
                              <span>Pièces Jointes ({docAttachments.length})</span>
                            </div>
                            <button
                              type="button"
                              title="Prendre une photo ou ajouter un document"
                              onClick={() => { setActiveActionModal('photo'); setPhotoDataUrl(''); }}
                              style={{
                                background: 'rgba(56, 189, 248, 0.15)',
                                border: '1px solid #38bdf8',
                                color: '#38bdf8',
                                padding: '4px 8px',
                                borderRadius: '6px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '3px',
                                cursor: 'pointer'
                              }}
                            >
                              <Plus size={12} />
                              <Camera size={14} />
                            </button>
                          </div>

                          {docAttachments.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                              {docAttachments.map(att => {
                                let label = 'Photo';
                                if (att.attachment_type === 'proof_of_payment_photo') label = '💳 Paiement';
                                else if (att.attachment_type === 'order_pdf') label = '📄 Bon PDF';

                                return (
                                  <div key={att.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#1e293b', border: '1px solid #334155', padding: '3px 8px', borderRadius: '6px', fontSize: '0.72rem', color: '#cbd5e1' }}>
                                    {att.value && (
                                      <a href={resolveAssetUrl(att.value)} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', color: '#38bdf8', textDecoration: 'none' }}>
                                        <img src={resolveAssetUrl(att.value)} alt={label} style={{ width: '16px', height: '16px', borderRadius: '2px', objectFit: 'cover', marginRight: '4px' }} />
                                      </a>
                                    )}
                                    <span>{label}</span>
                                    <button onClick={() => handleDeleteAttachment(att.id)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0 2px' }} title="Supprimer la pièce jointe">
                                      <X size={12} />
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {/* ENCADRÉ 6: PAIEMENT SUR PLACE */}
                        <div style={{ background: '#0f172a', padding: '8px 12px', borderRadius: '10px', border: '1px solid #334155', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <CreditCard size={14} color="#22c55e" />
                            <span style={{ fontSize: '0.75rem', fontWeight: '800', color: '#22c55e' }}>Paiement sur place</span>
                            {paymentType && paymentType !== 'none' && amountPaid && (
                              <span style={{ fontSize: '0.72rem', fontWeight: '700', color: '#ffffff', background: '#064e3b', border: '1px solid #22c55e', padding: '2px 8px', borderRadius: '6px' }}>
                                {paymentType === 'cash' ? '💵 Espèces' : '💳 Carte'} : {parseFloat(amountPaid || 0).toFixed(2)} CHF
                              </span>
                            )}
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {paymentType && paymentType !== 'none' && amountPaid && (
                              <button
                                type="button"
                                onClick={() => { setPaymentType('none'); setAmountPaid(''); }}
                                style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '2px' }}
                                title="Effacer le paiement"
                              >
                                <X size={14} />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => setActiveActionModal('payment')}
                              title={paymentType !== 'none' && amountPaid ? "Modifier le paiement" : "Ajouter un paiement sur place"}
                              style={{
                                background: 'rgba(34, 197, 94, 0.15)',
                                border: '1px solid #22c55e',
                                color: '#22c55e',
                                padding: '4px 8px',
                                borderRadius: '6px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '3px',
                                cursor: 'pointer'
                              }}
                            >
                              <Plus size={12} />
                              <CreditCard size={14} />
                            </button>
                          </div>
                        </div>

                        {/* ENCADRÉ 7: PROBLÈMES (MATCHING REFERENCE RED BOX) */}
                        {selectedProblemReasons.length === 0 ? (
                          <button
                            type="button"
                            onClick={() => setActiveActionModal('problem_reasons')}
                            style={{
                              width: '100%',
                              background: 'transparent',
                              border: '1px solid #ef4444',
                              color: '#ef4444',
                              padding: '10px 14px',
                              borderRadius: '8px',
                              fontWeight: '700',
                              fontSize: '0.8rem',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              cursor: 'pointer',
                              flexShrink: 0
                            }}
                          >
                            <span>▼</span>
                            <span>Pas de problème signalé</span>
                          </button>
                        ) : (
                          <div style={{ background: 'rgba(239, 68, 68, 0.15)', border: '1px solid #ef4444', borderRadius: '8px', padding: '10px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <AlertTriangle size={16} color="#ef4444" />
                              <div>
                                <div style={{ fontSize: '0.76rem', fontWeight: '800', color: '#ef4444' }}>Motif(s) d'échec signalé(s) :</div>
                                <div style={{ fontSize: '0.72rem', color: '#fca5a5', marginTop: '2px' }}>
                                  {selectedProblemReasons.join(', ')}
                                </div>
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button
                                type="button"
                                onClick={() => setActiveActionModal('problem_reasons')}
                                style={{ background: '#1e293b', border: '1px solid #ef4444', color: '#ef4444', padding: '4px 8px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: '700', cursor: 'pointer' }}
                              >
                                Modifier
                              </button>
                              <button
                                type="button"
                                onClick={() => setSelectedProblemReasons([])}
                                style={{ background: 'none', border: 'none', color: '#94a3b8', padding: '4px', cursor: 'pointer' }}
                                title="Effacer le motif"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          </div>
                        )}

                        {/* ENCADRÉ 8: REMARQUES DU LIVREUR (MATCHING REFERENCE) */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flexShrink: 0 }}>
                          <textarea
                            rows={2}
                            placeholder="Des commentaires particuliers pour cette livraison? Veuillez les noter ici..."
                            value={driverNotes}
                            onChange={(e) => setDriverNotes(e.target.value)}
                            style={{
                              width: '100%',
                              boxSizing: 'border-box',
                              background: '#0f172a',
                              border: '1px solid #334155',
                              color: '#fff',
                              padding: '8px 10px',
                              borderRadius: '8px',
                              fontSize: '0.76rem',
                              outline: 'none',
                              resize: 'vertical'
                            }}
                          />
                        </div>

                      </div>

                      {/* BOTTOM ACTION BAR: STOPWATCH ON LEFT + VALIDER LIVRAISON */}
                      <div style={{ background: '#0f172a', padding: '12px 16px', borderTop: '1px solid #334155', display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                        {/* STOPWATCH ICON BUTTON (MARK ARRIVAL) */}
                        <button
                          type="button"
                          disabled={loading || isValidated || !isMissionStarted}
                          onClick={() => handleMarkArrival(activeStopModal.id)}
                          title={hasArrival ? `Arrivé à ${formatLocalTimeHHMM(activeStopModal.actual_arrival)}` : (!isMissionStarted ? "Démarrer la tournée d'abord pour marquer l'arrivée" : "Marquer mon arrivée")}
                          style={{
                            width: '46px',
                            height: '46px',
                            borderRadius: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            background: hasArrival ? '#064e3b' : (!isMissionStarted ? '#334155' : '#0284c7'),
                            border: hasArrival ? '1px solid #22c55e' : (!isMissionStarted ? '1px solid #475569' : '1px solid #38bdf8'),
                            color: hasArrival ? '#22c55e' : (!isMissionStarted ? '#94a3b8' : '#ffffff'),
                            cursor: (loading || isValidated || !isMissionStarted) ? 'not-allowed' : 'pointer'
                          }}
                        >
                          <Timer size={22} />
                        </button>

                        {/* VALIDATE STOP BUTTON */}
                        <button
                          disabled={loading || !isMissionStarted}
                          onClick={() => handleCompleteStop(activeStopModal.id, selectedProblemReasons.length > 0 ? 'failed' : 'delivered')}
                          title={!isMissionStarted ? "Démarrer la tournée d'abord pour pouvoir valider ce stop" : ""}
                          style={{
                            flex: 1,
                            height: '46px',
                            background: !isMissionStarted ? '#334155' : (selectedProblemReasons.length > 0 ? '#ef4444' : '#22c55e'),
                            color: !isMissionStarted ? '#94a3b8' : '#ffffff',
                            border: 'none',
                            borderRadius: '10px',
                            fontWeight: '900',
                            fontSize: '0.88rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            cursor: (loading || !isMissionStarted) ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {selectedProblemReasons.length > 0 ? (
                            <>
                              <AlertTriangle size={18} />
                              <span>{activeStopModal.status === 'failed' ? "✓ Échec Validé" : "Valider l'Échec"}</span>
                            </>
                          ) : (
                            <>
                              <CheckCircle size={18} />
                              <span>{activeStopModal.status === 'delivered' ? '✓ Livraison Validée' : 'Valider Livraison'}</span>
                            </>
                          )}
                        </button>
                      </div>

                    </div>

                    {/* MODAL 1: CLIENT INFO POPUP */}
                    {showClientInfoModal && (
                      <div
                        style={{
                          position: 'fixed',
                          inset: 0,
                          background: 'rgba(0,0,0,0.75)',
                          backdropFilter: 'blur(3px)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 10005,
                          padding: '16px'
                        }}
                        onClick={() => setShowClientInfoModal(false)}
                      >
                        <div
                          style={{
                            background: '#1e293b',
                            border: '1px solid #38bdf8',
                            borderRadius: '12px',
                            width: '100%',
                            maxWidth: '420px',
                            maxHeight: '80vh',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.6)'
                          }}
                          onClick={e => e.stopPropagation()}
                        >
                          {/* Header */}
                          <div style={{ padding: '12px 16px', background: '#0f172a', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <Info size={16} color="#38bdf8" />
                              <span style={{ fontWeight: '800', fontSize: '0.88rem', color: '#ffffff' }}>
                                Infos Client — {activeStopModal.client_name}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => setShowClientInfoModal(false)}
                              style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px' }}
                            >
                              <X size={18} />
                            </button>
                          </div>

                          {/* Content */}
                          <div style={{ padding: '14px 16px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {/* Roadbook / Delivery Guide */}
                            {client?.roadbook && client.roadbook.trim() && (
                              <div style={{ background: '#0f172a', borderRadius: '8px', padding: '10px 12px', border: '1px solid #334155' }}>
                                <div style={{ fontSize: '0.72rem', fontWeight: '800', color: '#38bdf8', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                  📖 Guide de livraison / Roadbook
                                </div>
                                <div style={{ fontSize: '0.78rem', color: '#cbd5e1', whiteSpace: 'pre-wrap', lineHeight: '1.4' }}>
                                  {client.roadbook}
                                </div>
                              </div>
                            )}

                            {/* Contacts */}
                            {matchingContacts.length > 0 && (
                              <div style={{ background: '#0f172a', borderRadius: '8px', padding: '10px 12px', border: '1px solid #334155' }}>
                                <div style={{ fontSize: '0.72rem', fontWeight: '800', color: '#38bdf8', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                  👥 Contacts
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                  {matchingContacts.map((contact, idx) => (
                                    <div key={contact.id || idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: idx < matchingContacts.length - 1 ? '6px' : '0', borderBottom: idx < matchingContacts.length - 1 ? '1px dashed #1e293b' : 'none' }}>
                                      <div>
                                        <div style={{ fontSize: '0.78rem', fontWeight: '700', color: '#ffffff' }}>
                                          {contact.name || contact.first_name || 'Contact'} {contact.last_name || ''}
                                        </div>
                                        {contact.role && (
                                          <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                                            {contact.role}
                                          </div>
                                        )}
                                      </div>
                                      <div style={{ display: 'flex', gap: '8px' }}>
                                        {contact.phone && (
                                          <a
                                            href={`tel:${contact.phone}`}
                                            title={`Appeler ${contact.phone}`}
                                            style={{
                                              background: '#0284c7',
                                              color: '#fff',
                                              borderRadius: '6px',
                                              padding: '5px 8px',
                                              fontSize: '0.72rem',
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              gap: '4px',
                                              textDecoration: 'none',
                                              fontWeight: '700'
                                            }}
                                          >
                                            <Phone size={12} />
                                            <span>{contact.phone}</span>
                                          </a>
                                        )}
                                        {contact.email && (
                                          <a
                                            href={`mailto:${contact.email}`}
                                            title={`Envoyer un email à ${contact.email}`}
                                            style={{
                                              background: '#334155',
                                              color: '#cbd5e1',
                                              borderRadius: '6px',
                                              padding: '5px 8px',
                                              fontSize: '0.72rem',
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              textDecoration: 'none'
                                            }}
                                          >
                                            {contact.email}
                                          </a>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {!client?.roadbook && matchingContacts.length === 0 && (
                              <div style={{ fontSize: '0.75rem', color: '#64748b', textAlign: 'center', padding: '12px 0' }}>
                                Aucune information spécifique pour ce client.
                              </div>
                            )}
                          </div>

                          {/* Footer */}
                          <div style={{ padding: '10px 16px', background: '#0f172a', borderTop: '1px solid #334155', display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                              type="button"
                              onClick={() => setShowClientInfoModal(false)}
                              style={{ background: '#334155', border: 'none', color: '#ffffff', padding: '6px 14px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '700', cursor: 'pointer' }}
                            >
                              Fermer
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* MODAL 2: POST-IT POPUP */}
                    {showPostitModal && (
                      <div
                        style={{
                          position: 'fixed',
                          inset: 0,
                          background: 'rgba(0,0,0,0.75)',
                          backdropFilter: 'blur(3px)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 10005,
                          padding: '16px'
                        }}
                        onClick={() => setShowPostitModal(false)}
                      >
                        <div
                          style={{
                            background: '#1e293b',
                            border: '1px solid #ca8a04',
                            borderRadius: '12px',
                            width: '100%',
                            maxWidth: '400px',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.6)'
                          }}
                          onClick={e => e.stopPropagation()}
                        >
                          {/* Header */}
                          <div style={{ padding: '12px 16px', background: '#0f172a', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <FileText size={16} color="#facc15" />
                              <span style={{ fontWeight: '800', fontSize: '0.88rem', color: '#facc15' }}>
                                Consigne Chauffeur (Post-it)
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => setShowPostitModal(false)}
                              style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px' }}
                            >
                              <X size={18} />
                            </button>
                          </div>

                          {/* Body */}
                          <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                              Note interne persistante partagée entre tous les chauffeurs pour ce client ({activeStopModal.client_name}) :
                            </div>
                            <textarea
                              rows={4}
                              placeholder="Ex: Sonner à la barrière, code 4589, déposer au fond du couloir..."
                              value={clientPostitText}
                              onChange={e => setClientPostitText(e.target.value)}
                              style={{
                                width: '100%',
                                boxSizing: 'border-box',
                                background: 'rgba(254, 240, 138, 0.08)',
                                border: '1px solid #ca8a04',
                                color: '#fef08a',
                                borderRadius: '8px',
                                padding: '10px',
                                fontSize: '0.78rem',
                                resize: 'vertical',
                                outline: 'none'
                              }}
                            />
                          </div>

                          {/* Footer */}
                          <div style={{ padding: '10px 16px', background: '#0f172a', borderTop: '1px solid #334155', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                            <button
                              type="button"
                              onClick={() => setShowPostitModal(false)}
                              style={{ background: 'none', border: '1px solid #475569', color: '#94a3b8', padding: '6px 12px', borderRadius: '6px', fontSize: '0.75rem', cursor: 'pointer' }}
                            >
                              Annuler
                            </button>
                            <button
                              type="button"
                              disabled={savingPostit}
                              onClick={async () => {
                                await handleSaveClientPostit(activeStopModal.client_id);
                                setShowPostitModal(false);
                              }}
                              style={{
                                background: '#ca8a04',
                                color: '#ffffff',
                                border: 'none',
                                padding: '6px 16px',
                                borderRadius: '6px',
                                fontWeight: '800',
                                fontSize: '0.75rem',
                                cursor: savingPostit ? 'not-allowed' : 'pointer'
                              }}
                            >
                              {savingPostit ? 'Enregistrement...' : 'Enregistrer'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}



              {/* ---------------------------------------------------- */}
              {/* ACTION MENU MODAL SHEET (BLUE + BUTTON) */}
              {/* ---------------------------------------------------- */}
              {activeActionModal === 'action_menu' && activeStopModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '16px' }}>
                  <div style={{ background: '#1e293b', border: '1px solid #38bdf8', borderRadius: '14px', width: '100%', maxWidth: '400px', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.5)' }}>
                    <div style={{ background: '#0f172a', padding: '14px 16px', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: '800', fontSize: '0.92rem', color: '#38bdf8' }}>
                        ➕ Ajouter une action pour ce stop
                      </span>
                      <button onClick={() => setActiveActionModal(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>
                        <X size={18} />
                      </button>
                    </div>

                    <div style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {/* OPTION 1: PHOTO */}
                      <button
                        onClick={() => { setActiveActionModal('photo'); setPhotoDataUrl(''); }}
                        style={{ background: '#0f172a', border: '1px solid #334155', padding: '12px 14px', borderRadius: '10px', color: '#ffffff', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', textAlign: 'left' }}
                      >
                        <div style={{ background: 'rgba(56, 189, 248, 0.15)', padding: '8px', borderRadius: '8px' }}>
                          <Camera size={18} color="#38bdf8" />
                        </div>
                        <div>
                          <div style={{ fontWeight: '800', fontSize: '0.84rem' }}>Preuve Photo / Document</div>
                          <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Colis, quai, preuve de paiement, bon papier</div>
                        </div>
                      </button>

                      {/* OPTION 2: DRIVER SIGNATURE */}
                      <button
                        onClick={() => setActiveActionModal('driver_sig')}
                        style={{ background: '#0f172a', border: '1px solid #334155', padding: '12px 14px', borderRadius: '10px', color: '#ffffff', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', textAlign: 'left' }}
                      >
                        <div style={{ background: 'rgba(34, 197, 94, 0.15)', padding: '8px', borderRadius: '8px' }}>
                          <PenTool size={18} color="#22c55e" />
                        </div>
                        <div>
                          <div style={{ fontWeight: '800', fontSize: '0.84rem' }}>Signature du Chauffeur</div>
                          <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Attestation de livraison effectuée par le livreur</div>
                        </div>
                      </button>

                      {/* OPTION 3: RECIPIENT SIGNATURE */}
                      <button
                        onClick={() => {
                          const clientContactsMatching = clientContacts.filter(c => c.client_id === activeStopModal.client_id);
                          setRecipientContactId(clientContactsMatching[0]?.id ? String(clientContactsMatching[0].id) : 'other');
                          setRecipientCustomLastName('');
                          setRecipientCustomFirstName('');
                          setActiveActionModal('recipient_sig');
                        }}
                        style={{ background: '#0f172a', border: '1px solid #334155', padding: '12px 14px', borderRadius: '10px', color: '#ffffff', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', textAlign: 'left' }}
                      >
                        <div style={{ background: 'rgba(245, 158, 11, 0.15)', padding: '8px', borderRadius: '8px' }}>
                          <User size={18} color="#f59e0b" />
                        </div>
                        <div>
                          <div style={{ fontWeight: '800', fontSize: '0.84rem' }}>Signature du Client / Destinataire</div>
                          <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Sélection du contact client et signature tactile</div>
                        </div>
                      </button>

                      {/* OPTION 4: RETURN TICKET */}
                      <button
                        onClick={() => {
                          setTicketOrderId('');
                          setReturnTicketTab('returned');
                          setTicketLines(objects.map(o => ({ object_id: o.id, amount: 0, unit_price: parseFloat(o.price || 0) })));
                          setActiveActionModal('return_ticket');
                        }}
                        style={{ background: '#0f172a', border: '1px solid #334155', padding: '12px 14px', borderRadius: '10px', color: '#ffffff', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', textAlign: 'left' }}
                      >
                        <div style={{ background: 'rgba(168, 85, 247, 0.15)', padding: '8px', borderRadius: '8px' }}>
                          <Package size={18} color="#a855f7" />
                        </div>
                        <div>
                          <div style={{ fontWeight: '800', fontSize: '0.84rem' }}>Bon de Retour / Emballages & Articles</div>
                          <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Palettes consignées, emballages, modifs commande</div>
                        </div>
                      </button>

                      {/* OPTION 5: PAYMENT ON SITE */}
                      <button
                        onClick={() => setActiveActionModal('payment')}
                        style={{ background: '#0f172a', border: '1px solid #334155', padding: '12px 14px', borderRadius: '10px', color: '#ffffff', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', textAlign: 'left' }}
                      >
                        <div style={{ background: 'rgba(34, 197, 94, 0.15)', padding: '8px', borderRadius: '8px' }}>
                          <CreditCard size={18} color="#22c55e" />
                        </div>
                        <div>
                          <div style={{ fontWeight: '800', fontSize: '0.84rem' }}>Paiement sur Place</div>
                          <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Encaisser en espèces ou carte bancaire</div>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ---------------------------------------------------- */}
              {/* PAYMENT ON SITE MODAL */}
              {/* ---------------------------------------------------- */}
              {activeActionModal === 'payment' && activeStopModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '16px' }}>
                  <div style={{ background: '#1e293b', border: '1px solid #22c55e', borderRadius: '14px', width: '100%', maxWidth: '380px', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.5)' }}>
                    <div style={{ background: '#0f172a', padding: '12px 16px', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <CreditCard size={16} color="#22c55e" />
                        <span style={{ fontWeight: '800', fontSize: '0.88rem', color: '#22c55e' }}>Paiement sur Place</span>
                      </div>
                      <button onClick={() => setActiveActionModal(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>
                        <X size={18} />
                      </button>
                    </div>

                    <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div>
                        <label style={{ fontSize: '0.72rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Mode de règlement :</label>
                        <select
                          value={paymentType}
                          onChange={e => setPaymentType(e.target.value)}
                          style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', color: '#fff', borderRadius: '6px', padding: '8px 10px', fontSize: '0.8rem', outline: 'none' }}
                        >
                          <option value="none">Aucun (None)</option>
                          <option value="cash">Espèces (Cash)</option>
                          <option value="card">Carte bancaire (Card)</option>
                        </select>
                      </div>

                      <div>
                        <label style={{ fontSize: '0.72rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Montant encaissé (CHF) :</label>
                        <input
                          type="number"
                          step="0.05"
                          placeholder="0.00"
                          value={amountPaid}
                          onChange={e => setAmountPaid(e.target.value)}
                          style={{ width: '100%', boxSizing: 'border-box', background: '#0f172a', border: '1px solid #334155', color: '#22c55e', fontWeight: '700', borderRadius: '6px', padding: '8px 10px', fontSize: '0.84rem', outline: 'none' }}
                        />
                      </div>
                    </div>

                    <div style={{ padding: '12px 16px', background: '#0f172a', borderTop: '1px solid #334155', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                      <button
                        type="button"
                        onClick={() => setActiveActionModal(null)}
                        style={{ background: 'none', border: '1px solid #475569', color: '#94a3b8', padding: '6px 12px', borderRadius: '6px', fontSize: '0.75rem', cursor: 'pointer' }}
                      >
                        Annuler
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveActionModal(null)}
                        style={{ background: '#22c55e', color: '#ffffff', border: 'none', padding: '6px 16px', borderRadius: '6px', fontWeight: '800', fontSize: '0.75rem', cursor: 'pointer' }}
                      >
                        Enregistrer
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ---------------------------------------------------- */}
              {/* PHOTO UPLOAD MODAL */}
              {/* ---------------------------------------------------- */}
              {activeActionModal === 'photo' && activeStopModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '16px' }}>
                  <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '14px', width: '100%', maxWidth: '420px', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.5)' }}>
                    <div style={{ background: '#0f172a', padding: '12px 16px', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: '800', fontSize: '0.88rem', color: '#38bdf8' }}>📷 Téléverser une Photo / Preuve</span>
                      <button onClick={() => setActiveActionModal(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}><X size={18} /></button>
                    </div>

                    <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div>
                        <label style={{ fontSize: '0.72rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Type de document :</label>
                        <select
                          value={photoType}
                          onChange={e => setPhotoType(e.target.value)}
                          style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', color: '#fff', borderRadius: '6px', padding: '8px', fontSize: '0.78rem' }}
                        >
                          <option value="other_photo">Photo colis / lieu de dépose</option>
                          <option value="proof_of_payment_photo">Preuve de paiement (reçu / chèque)</option>
                          <option value="other_document">Autre document / bon papier</option>
                        </select>
                      </div>

                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        style={{ display: 'none' }}
                        onChange={e => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onload = () => setPhotoDataUrl(reader.result);
                            reader.readAsDataURL(file);
                          }
                        }}
                      />

                      {photoDataUrl ? (
                        <div style={{ textAlign: 'center' }}>
                          <img src={resolveAssetUrl(photoDataUrl)} alt="Preview" style={{ width: '100%', maxHeight: '200px', objectFit: 'contain', borderRadius: '8px', border: '1px solid #334155' }} />
                          <button
                            type="button"
                            onClick={handleTriggerPhotoCapture}
                            style={{ background: '#334155', color: '#cbd5e1', border: 'none', padding: '4px 10px', borderRadius: '4px', fontSize: '0.72rem', marginTop: '6px', cursor: 'pointer' }}
                          >
                            Changer l'image
                          </button>
                        </div>
                      ) : (
                        <div
                          onClick={handleTriggerPhotoCapture}
                          style={{ border: '2px dashed #38bdf8', borderRadius: '10px', padding: '30px 20px', textAlign: 'center', cursor: 'pointer', background: '#0f172a' }}
                        >
                          <Camera size={32} color="#38bdf8" style={{ marginBottom: '6px' }} />
                          <div style={{ fontWeight: '700', fontSize: '0.82rem', color: '#ffffff' }}>Prendre une photo ou choisir un fichier</div>
                          <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '2px' }}>Appareil photo / galerie</div>
                        </div>
                      )}

                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
                        <button onClick={() => setActiveActionModal(null)} style={{ background: 'none', border: '1px solid #475569', color: '#94a3b8', padding: '8px 14px', borderRadius: '6px', fontSize: '0.76rem', cursor: 'pointer' }}>Annuler</button>
                        <button
                          disabled={!photoDataUrl || loading}
                          onClick={() => handleUploadAndAttach(photoDataUrl, photoType, 'photo')}
                          style={{ background: photoDataUrl ? '#22c55e' : '#334155', color: '#ffffff', border: 'none', padding: '8px 16px', borderRadius: '6px', fontWeight: '800', fontSize: '0.76rem', cursor: photoDataUrl ? 'pointer' : 'not-allowed' }}
                        >
                          Enregistrer Photo
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ---------------------------------------------------- */}
              {/* DRIVER SIGNATURE MODAL */}
              {/* ---------------------------------------------------- */}
              {activeActionModal === 'driver_sig' && activeStopModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '16px' }}>
                  <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '14px', width: '100%', maxWidth: '440px', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.5)' }}>
                    <SignaturePadCanvas
                      title="✍️ Signature du Chauffeur"
                      subtitle={`${driver?.first_name} ${driver?.last_name}`}
                      onCancel={() => setActiveActionModal(null)}
                      onSave={(dataUrl) => handleUploadAndAttach(dataUrl, 'driver_signature', 'driver_sig')}
                      onCopySig={firstStopSigUrl ? handleCopyFirstStopSignature : null}
                      copySigLabel="Copier sig. 1er stop"
                    />
                  </div>
                </div>
              )}

              {/* ---------------------------------------------------- */}
              {/* RECIPIENT SIGNATURE MODAL */}
              {/* ---------------------------------------------------- */}
              {activeActionModal === 'recipient_sig' && activeStopModal && (() => {
                const matchingContacts = clientContacts.filter(c => c.client_id === activeStopModal.client_id);
                const selectedContact = matchingContacts.find(c => String(c.id) === recipientContactId);
                const effectiveSignerName = selectedContact
                  ? formatSignerName(selectedContact.last_name, selectedContact.first_name)
                  : formatSignerName(recipientCustomLastName, recipientCustomFirstName);

                return (
                  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '16px' }}>
                    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '14px', width: '100%', maxWidth: '440px', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.5)' }}>
                      <div style={{ padding: '14px 16px', background: '#0f172a', borderBottom: '1px solid #334155', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: '800', fontSize: '0.88rem', color: '#f59e0b' }}>🖋️ Signature Destinataire (Client)</span>
                          <button onClick={() => setActiveActionModal(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}><X size={18} /></button>
                        </div>

                        {/* CONTACT DROPDOWN LOOKUP */}
                        <div>
                          <label style={{ fontSize: '0.7rem', color: '#94a3b8', display: 'block', marginBottom: '2px' }}>Sélectionner le contact :</label>
                          <select
                            value={recipientContactId}
                            onChange={e => setRecipientContactId(e.target.value)}
                            style={{ width: '100%', background: '#1e293b', border: '1px solid #334155', color: '#fff', borderRadius: '6px', padding: '6px 8px', fontSize: '0.76rem' }}
                          >
                            {matchingContacts.map(c => (
                              <option key={c.id} value={String(c.id)}>
                                {formatSignerName(c.last_name, c.first_name)} {c.phone ? `(${c.phone})` : ''} {c.is_primary ? '★ Principal' : ''}
                              </option>
                            ))}
                            <option value="other">Autre / Nouveau signataire</option>
                          </select>
                        </div>

                        {recipientContactId === 'other' && (
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                            <div>
                              <label style={{ fontSize: '0.68rem', color: '#94a3b8', display: 'block', marginBottom: '2px' }}>Nom (MAJUSCULES) :</label>
                              <input
                                type="text"
                                placeholder="ex: DUPONT"
                                value={recipientCustomLastName}
                                onChange={e => setRecipientCustomLastName(e.target.value)}
                                style={{ width: '100%', boxSizing: 'border-box', background: '#1e293b', border: '1px solid #334155', color: '#fff', borderRadius: '6px', padding: '6px 8px', fontSize: '0.76rem' }}
                              />
                            </div>
                            <div>
                              <label style={{ fontSize: '0.68rem', color: '#94a3b8', display: 'block', marginBottom: '2px' }}>Prénom :</label>
                              <input
                                type="text"
                                placeholder="ex: Jean"
                                value={recipientCustomFirstName}
                                onChange={e => setRecipientCustomFirstName(e.target.value)}
                                style={{ width: '100%', boxSizing: 'border-box', background: '#1e293b', border: '1px solid #334155', color: '#fff', borderRadius: '6px', padding: '6px 8px', fontSize: '0.76rem' }}
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      <SignaturePadCanvas
                        title="Pad de signature client"
                        subtitle={effectiveSignerName ? `Signataire : ${effectiveSignerName}` : 'Sélectionnez ou renseignez un signataire'}
                        onCancel={() => setActiveActionModal(null)}
                        onCopySig={previousClientSigUrl ? handleCopyClientSignature : null}
                        copySigLabel={previousClientSignerName ? `Copier sig. (${previousClientSignerName})` : "Copier sig. précédente"}
                        onSave={async (dataUrl) => {
                          if (effectiveSignerName) {
                            try {
                              await apiFetch(`/api/stops/${activeStopModal.id}/attachments`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ attachment_type: 'recipient_signature_name', value: effectiveSignerName })
                              });
                            } catch (e) {}
                          }
                          await handleUploadAndAttach(dataUrl, 'recipient_signature', 'recipient_sig');
                        }}
                      />
                    </div>
                  </div>
                );
              })()}

              {/* ---------------------------------------------------- */}
              {/* TWO-TAB RETURN TICKET MODAL */}
              {/* ---------------------------------------------------- */}
              {activeActionModal === 'return_ticket' && (activeStopModal || editingTicketStopId) && (() => {
                const currentModalStop = activeStopModal || stops.find(s => s.id === editingTicketStopId);
                const currentModalClient = clients.find(c => c.id === currentModalStop?.client_id);
                const totalValue = ticketLines.reduce((acc, l) => acc + ((l.amount || 0) * (l.unit_price || 0)), 0);

                const returnedObjects = objects.filter(o => o.is_returned === true || o.is_returned === 'true' || o.is_returned === 1);
                const orderItemObjects = objects.filter(o => !o.is_returned || o.is_returned === false || o.is_returned === 'false' || o.is_returned === 0);

                const currentTabObjects = returnTicketTab === 'returned' ? returnedObjects : orderItemObjects;

                const handleAmountChange = (objId, delta) => {
                  setTicketLines(prev => {
                    const existing = prev.find(l => l.object_id === objId);
                    const obj = objects.find(o => o.id === objId);
                    const uPrice = parseFloat(obj?.price || 0);
                    if (existing) {
                      return prev.map(l => l.object_id === objId ? { ...l, amount: (l.amount || 0) + delta } : l);
                    } else {
                      return [...prev, { object_id: objId, amount: delta, unit_price: uPrice }];
                    }
                  });
                };

                const handleSetDirectAmount = (objId, newAmt) => {
                  setTicketLines(prev => {
                    const existing = prev.find(l => l.object_id === objId);
                    const obj = objects.find(o => o.id === objId);
                    const uPrice = parseFloat(obj?.price || 0);
                    if (existing) {
                      return prev.map(l => l.object_id === objId ? { ...l, amount: newAmt } : l);
                    } else {
                      return [...prev, { object_id: objId, amount: newAmt, unit_price: uPrice }];
                    }
                  });
                };

                const returnedLinesCount = ticketLines.filter(l => {
                  const o = returnedObjects.find(ro => ro.id === l.object_id);
                  return o && (l.amount || 0) !== 0;
                }).length;

                const orderLinesCount = ticketLines.filter(l => {
                  const o = orderItemObjects.find(oo => oo.id === l.object_id);
                  return o && (l.amount || 0) !== 0;
                }).length;

                return (
                  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '16px' }}>
                    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '14px', width: '100%', maxWidth: '460px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.5)' }}>
                      <div style={{ padding: '12px 16px', background: '#0f172a', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                        <div>
                          <span style={{ fontWeight: '800', fontSize: '0.88rem', color: '#a855f7' }}>
                            {editingTicketId ? `✏️ Modifier Bon #${editingTicketId}` : '📦 Bon de Retour'}
                          </span>
                          {currentModalClient && (
                            <div style={{ fontSize: '0.70rem', color: '#94a3b8', marginTop: '1px' }}>
                              {currentModalClient.name} {currentModalStop ? `(Stop #${currentModalStop.real_index || currentModalStop.estimated_index || currentModalStop.sequence_order || currentModalStop.id})` : ''}
                            </div>
                          )}
                        </div>
                        <button onClick={() => { setActiveActionModal(null); setEditingTicketId(null); setEditingTicketStopId(null); }} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}><X size={18} /></button>
                      </div>

                      {/* OPTIONAL ORDER ID / REFERENCE INPUT */}
                      <div style={{ padding: '8px 16px', background: '#131d31', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                        <span style={{ fontSize: '0.72rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>N° Cde / Réf :</span>
                        <input
                          type="text"
                          placeholder="Ex: 10452 (optionnel)"
                          value={ticketOrderId}
                          onChange={(e) => setTicketOrderId(e.target.value)}
                          style={{
                            flex: 1,
                            background: '#0f172a',
                            border: '1px solid #334155',
                            borderRadius: '6px',
                            color: '#f8fafc',
                            padding: '4px 8px',
                            fontSize: '0.75rem',
                            outline: 'none'
                          }}
                        />
                      </div>

                      {/* TWO TABS (RETURNED VS ORDER ITEMS) */}
                      <div style={{ display: 'flex', background: '#0f172a', borderBottom: '1px solid #334155', padding: '0 8px', flexShrink: 0 }}>
                        <button
                          type="button"
                          onClick={() => setReturnTicketTab('returned')}
                          style={{
                            flex: 1,
                            background: returnTicketTab === 'returned' ? '#1e293b' : 'transparent',
                            color: returnTicketTab === 'returned' ? '#22c55e' : '#94a3b8',
                            border: 'none',
                            borderBottom: returnTicketTab === 'returned' ? '2px solid #22c55e' : 'none',
                            padding: '10px',
                            fontWeight: '800',
                            fontSize: '0.74rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px'
                          }}
                        >
                          <span>📦 Consignes</span>
                          {returnedLinesCount > 0 && (
                            <span style={{ background: '#22c55e', color: '#0f172a', borderRadius: '50%', width: '16px', height: '16px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem' }}>
                              {returnedLinesCount}
                            </span>
                          )}
                        </button>

                        <button
                          type="button"
                          onClick={() => setReturnTicketTab('order_items')}
                          style={{
                            flex: 1,
                            background: returnTicketTab === 'order_items' ? '#1e293b' : 'transparent',
                            color: returnTicketTab === 'order_items' ? '#38bdf8' : '#94a3b8',
                            border: 'none',
                            borderBottom: returnTicketTab === 'order_items' ? '2px solid #38bdf8' : 'none',
                            padding: '10px',
                            fontWeight: '800',
                            fontSize: '0.74rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px'
                          }}
                        >
                          <span>🏷️ Articles Commande</span>
                          {orderLinesCount > 0 && (
                            <span style={{ background: '#38bdf8', color: '#0f172a', borderRadius: '50%', width: '16px', height: '16px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem' }}>
                              {orderLinesCount}
                            </span>
                          )}
                        </button>
                      </div>

                      <div style={{ flex: 1, minHeight: 0, padding: '14px 16px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div style={{ fontSize: '0.72rem', color: '#94a3b8', flexShrink: 0 }}>
                          {returnTicketTab === 'returned'
                            ? "Renseignez les emballages consignés (+ repris chez le client, - déchargés) :"
                            : "Renseignez les articles de la commande modifiés (+ ajoutés / retournés, - manquants) :"}
                        </div>

                        {currentTabObjects.length === 0 ? (
                          <div style={{ color: '#94a3b8', fontSize: '0.74rem', fontStyle: 'italic', textAlign: 'center', padding: '20px', flexShrink: 0 }}>
                            Aucun élément enregistré dans cette catégorie.
                          </div>
                        ) : (
                          currentTabObjects.map(obj => {
                            const line = ticketLines.find(l => l.object_id === obj.id);
                            const amt = line ? line.amount : 0;
                            const uPrice = parseFloat(obj.price || 0);

                            return (
                              <div key={obj.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#0f172a', padding: '8px 12px', borderRadius: '8px', border: '1px solid #334155', flexShrink: 0 }}>
                                <div style={{ flex: 1, minWidth: 0, paddingRight: '8px' }}>
                                  <div style={{ fontWeight: '700', fontSize: '0.78rem', color: '#ffffff', wordBreak: 'break-word' }}>{obj.name}</div>
                                  <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                                    {uPrice.toFixed(2)} CHF / unité • {parseFloat(obj.weight_per_unit || 0).toFixed(1)} kg
                                  </div>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                                  <button
                                    type="button"
                                    onClick={() => handleAmountChange(obj.id, -1)}
                                    style={{ width: '28px', height: '28px', borderRadius: '6px', background: '#334155', color: '#fff', border: 'none', fontWeight: '900', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                  >
                                    -
                                  </button>
                                  <input
                                    type="number"
                                    value={amt === 0 ? '' : amt}
                                    placeholder="0"
                                    onChange={(e) => {
                                      const val = e.target.value === '' ? 0 : parseInt(e.target.value, 10);
                                      handleSetDirectAmount(obj.id, isNaN(val) ? 0 : val);
                                    }}
                                    style={{
                                      width: '44px',
                                      height: '28px',
                                      textAlign: 'center',
                                      fontWeight: '800',
                                      fontSize: '0.84rem',
                                      color: amt > 0 ? '#22c55e' : amt < 0 ? '#ef4444' : '#94a3b8',
                                      background: '#1e293b',
                                      border: '1px solid #334155',
                                      borderRadius: '6px',
                                      outline: 'none',
                                      padding: '0 4px'
                                    }}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => handleAmountChange(obj.id, +1)}
                                    style={{ width: '28px', height: '28px', borderRadius: '6px', background: '#38bdf8', color: '#0f172a', border: 'none', fontWeight: '900', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                  >
                                    +
                                  </button>
                                </div>
                              </div>
                            );
                          })
                        )}

                        <div style={{ background: '#0f172a', padding: '10px 12px', borderRadius: '8px', border: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                          <span style={{ fontSize: '0.76rem', color: '#cbd5e1', fontWeight: '700' }}>Valeur Totale Validée :</span>
                          <span style={{ fontSize: '0.92rem', fontWeight: '900', color: totalValue !== 0 ? '#22c55e' : '#94a3b8' }}>
                            {totalValue.toFixed(2)} CHF
                          </span>
                        </div>
                      </div>

                      <div style={{ padding: '12px 16px', background: '#0f172a', borderTop: '1px solid #334155', display: 'flex', justifyContent: 'flex-end', gap: '8px', flexShrink: 0 }}>
                        <button
                          type="button"
                          onClick={() => {
                            setActiveActionModal(null);
                            setEditingTicketId(null);
                            setEditingTicketStopId(null);
                          }}
                          style={{ background: 'none', border: '1px solid #475569', color: '#94a3b8', padding: '8px 14px', borderRadius: '6px', fontSize: '0.76rem', cursor: 'pointer' }}
                        >
                          Annuler
                        </button>
                        <button
                          disabled={loading}
                          onClick={handleSaveReturnTicket}
                          style={{ background: '#22c55e', color: '#ffffff', border: 'none', padding: '8px 16px', borderRadius: '6px', fontWeight: '800', fontSize: '0.76rem', cursor: 'pointer' }}
                        >
                          {editingTicketId ? 'Enregistrer les modifications' : 'Valider le Bon de Retour'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* ---------------------------------------------------- */}
              {/* PROBLEM REASONS MULTI-SELECT MODAL */}
              {/* ---------------------------------------------------- */}
              {activeActionModal === 'problem_reasons' && activeStopModal && (() => {
                const toggleReason = (label) => {
                  setSelectedProblemReasons(prev =>
                    prev.includes(label) ? prev.filter(r => r !== label) : [...prev, label]
                  );
                };

                return (
                  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '16px' }}>
                    <div style={{ background: '#1e293b', border: '1px solid #ef4444', borderRadius: '14px', width: '100%', maxWidth: '440px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.5)' }}>
                      <div style={{ padding: '12px 16px', background: '#0f172a', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: '800', fontSize: '0.88rem', color: '#ef4444' }}>⚠️ Signaler un Problème / Échec</span>
                        <button onClick={() => setActiveActionModal(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}><X size={18} /></button>
                      </div>

                      <div style={{ padding: '14px 16px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div style={{ fontSize: '0.74rem', color: '#94a3b8' }}>
                          Sélectionnez une ou plusieurs raisons pour expliquer l'échec de la livraison :
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '6px' }}>
                          {commonProblemReasons.map(r => {
                            const isSelected = selectedProblemReasons.includes(r.label);
                            return (
                              <button
                                key={r.id}
                                type="button"
                                onClick={() => toggleReason(r.label)}
                                style={{
                                  background: isSelected ? 'rgba(239, 68, 68, 0.2)' : '#0f172a',
                                  border: `1px solid ${isSelected ? '#ef4444' : '#334155'}`,
                                  borderRadius: '8px',
                                  padding: '8px 12px',
                                  color: isSelected ? '#ef4444' : '#cbd5e1',
                                  fontWeight: isSelected ? '800' : '500',
                                  fontSize: '0.76rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  cursor: 'pointer',
                                  textAlign: 'left'
                                }}
                              >
                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span>{r.icon}</span>
                                  <span>{r.label}</span>
                                </span>
                                {isSelected && <Check size={14} color="#ef4444" />}
                              </button>
                            );
                          })}
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
                          <label style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: '700' }}>Remarques complémentaires :</label>
                          <textarea
                            rows={3}
                            placeholder="Détails supplémentaires..."
                            value={driverNotes}
                            onChange={e => setDriverNotes(e.target.value)}
                            style={{ width: '100%', boxSizing: 'border-box', background: '#0f172a', border: '1px solid #334155', color: '#fff', borderRadius: '6px', padding: '8px', fontSize: '0.76rem', resize: 'vertical' }}
                          />
                        </div>
                      </div>

                      <div style={{ padding: '12px 16px', background: '#0f172a', borderTop: '1px solid #334155', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                        <button onClick={() => setActiveActionModal(null)} style={{ background: 'none', border: '1px solid #475569', color: '#94a3b8', padding: '8px 14px', borderRadius: '6px', fontSize: '0.76rem', cursor: 'pointer' }}>Fermer</button>
                        <button
                          type="button"
                          onClick={() => setActiveActionModal(null)}
                          style={{
                            background: selectedProblemReasons.length > 0 ? '#ef4444' : '#334155',
                            color: '#ffffff',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            fontWeight: '800',
                            fontSize: '0.76rem',
                            cursor: 'pointer'
                          }}
                        >
                          {selectedProblemReasons.length > 0 ? 'Enregistrer les motifs' : 'OK'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* ---------------------------------------------------- */}
              {/* GEOHISTORY GPS DEBUG MODAL */}
              {/* ---------------------------------------------------- */}
              {showGeohistoryDebugModal && currentJob && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '16px' }}>
                  <div style={{ background: '#1e293b', border: '1px solid #f59e0b', borderRadius: '14px', width: '100%', maxWidth: '580px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.6)' }}>
                    
                    {/* MODAL HEADER */}
                    <div style={{ padding: '12px 16px', background: '#0f172a', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '1rem' }}>🛰️</span>
                        <span style={{ fontWeight: '800', fontSize: '0.9rem', color: '#f59e0b' }}>
                          Inspecteur Géohistoire GPS ({geohistory.length} points)
                        </span>
                      </div>
                      <button onClick={() => setShowGeohistoryDebugModal(false)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px' }}>
                        <X size={18} />
                      </button>
                    </div>

                    {/* METRICS SUMMARY BAR */}
                    <div style={{ background: '#0f172a', padding: '10px 16px', borderBottom: '1px solid #334155', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', fontSize: '0.72rem', flexShrink: 0 }}>
                      <div>
                        <span style={{ color: '#94a3b8', display: 'block', fontSize: '0.66rem' }}>Total points :</span>
                        <b style={{ color: '#f59e0b', fontSize: '0.84rem' }}>{geohistory.length}</b>
                      </div>
                      <div>
                        <span style={{ color: '#94a3b8', display: 'block', fontSize: '0.66rem' }}>Premier :</span>
                        <b style={{ color: '#cbd5e1' }}>
                          {geohistory.length > 0 && geohistory[0].recorded_at
                            ? new Date(geohistory[0].recorded_at).toLocaleTimeString()
                            : '-'}
                        </b>
                      </div>
                      <div>
                        <span style={{ color: '#94a3b8', display: 'block', fontSize: '0.66rem' }}>Dernier :</span>
                        <b style={{ color: '#cbd5e1' }}>
                          {geohistory.length > 0 && geohistory[geohistory.length - 1].recorded_at
                            ? new Date(geohistory[geohistory.length - 1].recorded_at).toLocaleTimeString()
                            : '-'}
                        </b>
                      </div>
                      <div>
                        <span style={{ color: '#94a3b8', display: 'block', fontSize: '0.66rem' }}>Dernière vitesse :</span>
                        <b style={{ color: '#22c55e' }}>
                          {geohistory.length > 0 && geohistory[geohistory.length - 1].speed !== null
                            ? `${Math.round(geohistory[geohistory.length - 1].speed)} km/h`
                            : '-'}
                        </b>
                      </div>
                    </div>

                    {/* TABLE BODY (SCROLLABLE) */}
                    <div style={{ flex: 1, minHeight: 0, padding: '12px 16px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {geohistory.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '30px 16px', color: '#94a3b8', fontSize: '0.76rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '1.6rem' }}>📡</span>
                          <span>Aucun point GPS enregistré pour cette tournée.</span>
                          <span style={{ fontSize: '0.7rem', color: '#64748b' }}>
                            Le suivi GPS d'arrière-plan enregistre la position automatiquement dès qu'une tournée est démarrée.
                          </span>
                        </div>
                      ) : (
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem', textAlign: 'left' }}>
                            <thead>
                              <tr style={{ background: '#0f172a', borderBottom: '1px solid #334155', color: '#94a3b8' }}>
                                <th style={{ padding: '6px 8px' }}>#</th>
                                <th style={{ padding: '6px 8px' }}>Heure</th>
                                <th style={{ padding: '6px 8px' }}>Date</th>
                                <th style={{ padding: '6px 8px' }}>Latitude</th>
                                <th style={{ padding: '6px 8px' }}>Longitude</th>
                                <th style={{ padding: '6px 8px' }}>Vitesse</th>
                                <th style={{ padding: '6px 8px' }}>Précision</th>
                              </tr>
                            </thead>
                            <tbody>
                              {geohistory.map((pt, idx) => {
                                const dateObj = pt.recorded_at ? new Date(pt.recorded_at) : null;
                                const timeStr = dateObj ? dateObj.toLocaleTimeString() : '-';
                                const dateStr = dateObj ? dateObj.toLocaleDateString() : '-';
                                const isLatest = idx === geohistory.length - 1;

                                return (
                                  <tr
                                    key={pt.id || idx}
                                    style={{
                                      background: isLatest ? 'rgba(245, 158, 11, 0.15)' : (idx % 2 === 0 ? '#1e293b' : '#0f172a'),
                                      borderBottom: '1px solid #334155'
                                    }}
                                  >
                                    <td style={{ padding: '6px 8px', color: isLatest ? '#f59e0b' : '#64748b', fontWeight: '800' }}>
                                      {idx + 1} {isLatest ? '★' : ''}
                                    </td>
                                    <td style={{ padding: '6px 8px', fontWeight: '700', color: isLatest ? '#ffffff' : '#cbd5e1', whiteSpace: 'nowrap' }}>
                                      {timeStr}
                                    </td>
                                    <td style={{ padding: '6px 8px', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                                      {dateStr}
                                    </td>
                                    <td style={{ padding: '6px 8px', fontFamily: 'monospace', color: '#38bdf8' }}>
                                      {typeof pt.latitude === 'number' ? pt.latitude.toFixed(6) : parseFloat(pt.latitude || 0).toFixed(6)}
                                    </td>
                                    <td style={{ padding: '6px 8px', fontFamily: 'monospace', color: '#38bdf8' }}>
                                      {typeof pt.longitude === 'number' ? pt.longitude.toFixed(6) : parseFloat(pt.longitude || 0).toFixed(6)}
                                    </td>
                                    <td style={{ padding: '6px 8px', color: pt.speed > 0 ? '#22c55e' : '#94a3b8' }}>
                                      {pt.speed !== null && pt.speed !== undefined ? `${Math.round(pt.speed)} km/h` : '-'}
                                    </td>
                                    <td style={{ padding: '6px 8px', color: '#94a3b8' }}>
                                      {pt.accuracy !== null && pt.accuracy !== undefined ? `±${Math.round(pt.accuracy)}m` : '-'}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    {/* MODAL FOOTER */}
                    <div style={{ padding: '12px 16px', background: '#0f172a', borderTop: '1px solid #334155', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', flexShrink: 0 }}>
                      <button
                        type="button"
                        onClick={() => setShowGeohistoryDebugModal(false)}
                        style={{ background: '#0284c7', border: 'none', color: '#ffffff', padding: '6px 16px', borderRadius: '6px', fontSize: '0.74rem', fontWeight: '800', cursor: 'pointer' }}
                      >
                        Fermer
                      </button>
                    </div>

                  </div>
                </div>
              )}

              {/* ---------------------------------------------------- */}
              {/* TRUCK CONTENT & EMPTY RETURNS VERIFICATION MODAL */}
              {/* ---------------------------------------------------- */}
              {showTruckContentModal && (
                <div style={{
                  position: 'fixed',
                  inset: 0,
                  background: 'rgba(0,0,0,0.85)',
                  backdropFilter: 'blur(4px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 9999,
                  padding: '12px'
                }}>
                  <div style={{
                    background: '#1e293b',
                    border: '1px solid #334155',
                    borderRadius: '14px',
                    width: '100%',
                    maxWidth: '480px',
                    maxHeight: '92vh',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    boxShadow: '0 20px 25px -5px rgba(0,0,0,0.5)'
                  }}>
                    {/* MODAL HEADER */}
                    <div style={{
                      background: '#0f172a',
                      padding: '14px 16px',
                      borderBottom: '1px solid #334155',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexShrink: 0
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '8px',
                          background: 'rgba(56, 189, 248, 0.15)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Package size={18} color="#38bdf8" />
                        </div>
                        <div>
                          <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '800', color: '#ffffff' }}>
                            Vérification Retours & Camion
                          </h3>
                          <div style={{ fontSize: '0.70rem', color: '#94a3b8' }}>
                            Tournée #{currentJob.id} • {driver?.first_name} {driver?.last_name}
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowTruckContentModal(false)}
                        style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px' }}
                      >
                        <X size={20} />
                      </button>
                    </div>

                    {/* SUMMARY STATS ROW */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr',
                      gap: '8px',
                      padding: '12px 16px',
                      background: '#131d31',
                      borderBottom: '1px solid #334155',
                      flexShrink: 0
                    }}>
                      <div style={{ background: '#0f172a', padding: '8px', borderRadius: '8px', border: '1px solid #334155', textAlign: 'center' }}>
                        <div style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: '600' }}>Objets Repris</div>
                        <div style={{ fontSize: '1.05rem', fontWeight: '900', color: '#38bdf8', marginTop: '2px' }}>
                          {truckReturnedTotals.totalItemsCount}
                        </div>
                      </div>
                      <div style={{ background: '#0f172a', padding: '8px', borderRadius: '8px', border: '1px solid #334155', textAlign: 'center' }}>
                        <div style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: '600' }}>Valeur Consignes</div>
                        <div style={{ fontSize: '1.05rem', fontWeight: '900', color: '#22c55e', marginTop: '2px' }}>
                          {truckReturnedTotals.totalValue.toFixed(2)} <span style={{ fontSize: '0.65rem' }}>CHF</span>
                        </div>
                      </div>
                      <div style={{ background: '#0f172a', padding: '8px', borderRadius: '8px', border: '1px solid #334155', textAlign: 'center' }}>
                        <div style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: '600' }}>Bons Clients</div>
                        <div style={{ fontSize: '1.05rem', fontWeight: '900', color: '#f59e0b', marginTop: '2px' }}>
                          {jobReturnTickets.length}
                        </div>
                      </div>
                    </div>

                    {/* TOGGLE TABS (SYNTHÈSE TOTAUX VS BONS DÉTAILLÉS) */}
                    <div style={{ display: 'flex', background: '#0f172a', borderBottom: '1px solid #334155', padding: '0 8px', flexShrink: 0 }}>
                      <button
                        type="button"
                        onClick={() => setTruckTab('totals')}
                        style={{
                          flex: 1,
                          background: truckTab === 'totals' ? '#1e293b' : 'transparent',
                          color: truckTab === 'totals' ? '#38bdf8' : '#94a3b8',
                          border: 'none',
                          borderBottom: truckTab === 'totals' ? '2px solid #38bdf8' : 'none',
                          padding: '10px 8px',
                          fontWeight: '800',
                          fontSize: '0.74rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px'
                        }}
                      >
                        <span>📊 Totaux par objet</span>
                        {truckReturnedTotals.items.length > 0 && (
                          <span style={{ background: '#38bdf8', color: '#0f172a', borderRadius: '50%', width: '16px', height: '16px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: '900' }}>
                            {truckReturnedTotals.items.length}
                          </span>
                        )}
                      </button>

                      <button
                        type="button"
                        onClick={() => setTruckTab('clients')}
                        style={{
                          flex: 1,
                          background: truckTab === 'clients' ? '#1e293b' : 'transparent',
                          color: truckTab === 'clients' ? '#a855f7' : '#94a3b8',
                          border: 'none',
                          borderBottom: truckTab === 'clients' ? '2px solid #a855f7' : 'none',
                          padding: '10px 8px',
                          fontWeight: '800',
                          fontSize: '0.74rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px'
                        }}
                      >
                        <span>📋 Bons détaillés clients</span>
                        {jobReturnTickets.length > 0 && (
                          <span style={{ background: '#a855f7', color: '#ffffff', borderRadius: '50%', width: '16px', height: '16px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: '900' }}>
                            {jobReturnTickets.length}
                          </span>
                        )}
                      </button>
                    </div>

                    {/* TAB 1: TOTALS PER OBJECT */}
                    {truckTab === 'totals' && (
                      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {truckReturnedTotals.items.length === 0 ? (
                          <div style={{ textAlign: 'center', padding: '36px 16px', color: '#94a3b8' }}>
                            <Package size={36} color="#475569" style={{ margin: '0 auto 10px auto', display: 'block' }} />
                            <div style={{ fontWeight: '700', fontSize: '0.85rem', color: '#cbd5e1' }}>Aucun objet consigné ou retour</div>
                            <div style={{ fontSize: '0.72rem', marginTop: '4px' }}>
                              Les objets et consignes enregistrés sur chaque stop apparaîtront ici avec les totaux cumulés.
                            </div>
                          </div>
                        ) : (
                          truckReturnedTotals.items.map((item, idx) => (
                            <div
                              key={item.object_id || idx}
                              style={{
                                background: '#0f172a',
                                border: '1px solid #334155',
                                borderRadius: '10px',
                                padding: '10px 12px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '6px'
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                                <div>
                                  <div style={{ fontWeight: '800', fontSize: '0.82rem', color: '#ffffff' }}>
                                    {item.name}
                                  </div>
                                  <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                                    Prix unitaire : {item.unit_price.toFixed(2)} CHF
                                    {item.weight_per_unit > 0 && ` • ${item.weight_per_unit} kg/u`}
                                  </div>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span style={{
                                    background: item.total_amount > 0 ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                                    border: `1px solid ${item.total_amount > 0 ? '#22c55e' : '#ef4444'}`,
                                    color: item.total_amount > 0 ? '#22c55e' : '#ef4444',
                                    padding: '3px 8px',
                                    borderRadius: '6px',
                                    fontWeight: '900',
                                    fontSize: '0.82rem'
                                  }}>
                                    {item.total_amount > 0 ? `+${item.total_amount}` : item.total_amount}
                                  </span>
                                  <span style={{ fontWeight: '900', fontSize: '0.82rem', color: '#22c55e', minWidth: '65px', textAlign: 'right' }}>
                                    {item.total_price.toFixed(2)} CHF
                                  </span>
                                </div>
                              </div>

                              {/* CLIENT BREAKDOWN */}
                              {item.client_breakdown && item.client_breakdown.length > 0 && (
                                <div style={{
                                  marginTop: '4px',
                                  paddingTop: '6px',
                                  borderTop: '1px dashed #334155',
                                  fontSize: '0.68rem',
                                  color: '#94a3b8',
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: '6px'
                                }}>
                                  {item.client_breakdown.map((cb, cbIdx) => (
                                    <span key={cbIdx} style={{ background: '#1e293b', padding: '2px 6px', borderRadius: '4px', border: '1px solid #334155' }}>
                                      <span style={{ color: '#cbd5e1' }}>{cb.stop_label} :</span>{' '}
                                      <b style={{ color: cb.amount > 0 ? '#22c55e' : '#ef4444' }}>
                                        {cb.amount > 0 ? `+${cb.amount}` : cb.amount}
                                      </b>
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    )}

                    {/* TAB 2: DETAILED RETURN TICKETS PER CLIENT */}
                    {truckTab === 'clients' && (
                      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {jobReturnTickets.length === 0 ? (
                          <div style={{ textAlign: 'center', padding: '36px 16px', color: '#94a3b8' }}>
                            <Package size={36} color="#475569" style={{ margin: '0 auto 10px auto', display: 'block' }} />
                            <div style={{ fontWeight: '700', fontSize: '0.85rem', color: '#cbd5e1' }}>Aucun bon de retour</div>
                            <div style={{ fontSize: '0.72rem', marginTop: '4px' }}>
                              Aucun bon de retour validé sur les stops de cette tournée.
                            </div>
                          </div>
                        ) : (
                          jobReturnTickets.map(ticket => {
                            const stop = currentJobStops.find(s => s.id === ticket.stop_id);
                            const client = clients.find(c => c.id === stop?.client_id);
                            const stopIdx = currentJobStops.findIndex(s => s.id === ticket.stop_id);
                            const stopNum = stop?.real_index || stop?.estimated_index || (stopIdx >= 0 ? stopIdx + 1 : ticket.stop_id);
                            const valTime = ticket.validation_timestamp ? formatLocalTimeHHMM(ticket.validation_timestamp) : null;

                            return (
                              <div
                                key={ticket.id}
                                style={{
                                  background: '#0f172a',
                                  border: '1px solid #334155',
                                  borderRadius: '10px',
                                  padding: '10px 12px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '8px'
                                }}
                              >
                                {/* TICKET CLIENT HEADER */}
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <div style={{
                                      width: '22px',
                                      height: '22px',
                                      borderRadius: '50%',
                                      background: '#0284c7',
                                      color: '#ffffff',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '0.68rem',
                                      fontWeight: '800'
                                    }}>
                                      {stopNum}
                                    </div>
                                    <div>
                                      <div style={{ fontWeight: '800', fontSize: '0.82rem', color: '#ffffff' }}>
                                        {client?.name || stop?.client_name || 'Client inconnu'}
                                      </div>
                                      <div style={{ fontSize: '0.67rem', color: '#94a3b8' }}>
                                        {stop?.address || client?.address || ''}
                                        {valTime && ` • Validé à ${valTime}`}
                                        {ticket.order_id && ` • Cde: ${ticket.order_id}`}
                                      </div>
                                    </div>
                                  </div>

                                  <span style={{ fontWeight: '900', fontSize: '0.84rem', color: '#22c55e' }}>
                                    {parseFloat(ticket.validated_total_value || 0).toFixed(2)} CHF
                                  </span>
                                </div>

                                {/* TICKET LINES */}
                                <div style={{ background: '#1e293b', borderRadius: '6px', padding: '6px 8px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                  {ticket.lines && ticket.lines.length > 0 ? (
                                    ticket.lines.map((l, lIdx) => {
                                      const objDef = objects.find(o => o.id === l.object_id);
                                      const objName = l.object_name || objDef?.name || 'Objet';
                                      return (
                                        <div key={lIdx} style={{ fontSize: '0.70rem', color: '#cbd5e1', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                          <span>• {objName}</span>
                                          <span style={{ fontWeight: '700', color: l.amount > 0 ? '#22c55e' : '#cbd5e1' }}>
                                            {l.amount > 0 ? `+${l.amount}` : l.amount} × {parseFloat(l.unit_price || 0).toFixed(2)} CHF = {((l.amount || 0) * (l.unit_price || 0)).toFixed(2)} CHF
                                          </span>
                                        </div>
                                      );
                                    })
                                  ) : (
                                    <div style={{ fontSize: '0.68rem', color: '#94a3b8', fontStyle: 'italic' }}>Aucune ligne d'objet</div>
                                  )}
                                </div>

                                {/* ACTIONS: MODIFIER & SUPPRIMER */}
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '4px', borderTop: '1px dashed #334155' }}>
                                  <span style={{ fontSize: '0.68rem', color: '#94a3b8' }}>Bon #{ticket.id}</span>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <button
                                      type="button"
                                      onClick={() => handleOpenEditReturnTicket(ticket)}
                                      style={{
                                        background: 'rgba(56, 189, 248, 0.15)',
                                        border: '1px solid #0284c7',
                                        color: '#38bdf8',
                                        padding: '3px 8px',
                                        borderRadius: '5px',
                                        fontSize: '0.70rem',
                                        fontWeight: '700',
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '3px'
                                      }}
                                    >
                                      <Edit3 size={11} />
                                      <span>Modifier</span>
                                    </button>

                                    <button
                                      type="button"
                                      onClick={() => handleDeleteReturnTicket(ticket.id)}
                                      style={{
                                        background: 'rgba(239, 68, 68, 0.15)',
                                        border: '1px solid #dc2626',
                                        color: '#f87171',
                                        padding: '3px 8px',
                                        borderRadius: '5px',
                                        fontSize: '0.70rem',
                                        fontWeight: '700',
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '3px'
                                      }}
                                    >
                                      <Trash2 size={11} />
                                      <span>Supprimer</span>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}

                    {/* MODAL FOOTER */}
                    <div style={{
                      padding: '12px 16px',
                      background: '#0f172a',
                      borderTop: '1px solid #334155',
                      display: 'flex',
                      justifyContent: 'flex-end',
                      flexShrink: 0
                    }}>
                      <button
                        type="button"
                        onClick={() => setShowTruckContentModal(false)}
                        style={{
                          background: '#334155',
                          border: 'none',
                          color: '#ffffff',
                          padding: '8px 18px',
                          borderRadius: '8px',
                          fontSize: '0.78rem',
                          fontWeight: '800',
                          cursor: 'pointer'
                        }}
                      >
                        Fermer
                      </button>
                    </div>
                  </div>
                </div>
              )}

            </div>
          );
        })()}

      </div>
    </div>
  );
}
